import { useEffect, useMemo, useRef, useState } from "react";
import { BusyLabel } from "@/components/loading";
import { EditProfileCard } from "@/components/EditProfileCard";
import { NavLink, Routes, Route, Navigate, Link } from "react-router-dom";
import { api, cardImageUrl, cardName } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useDialog } from "@/lib/dialog";
import { usePublicConfig } from "@/lib/publicConfig";
import { useLightbox } from "@/lib/lightbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { AuthImg } from "@/components/AuthImg";
import { Users, Package, AlertTriangle, DollarSign, Scale, ShieldAlert, Settings, ClipboardList, Ban, Flag, Trash2, EyeOff, Eye, Download, Sparkles, Layers, XCircle, X, LayoutDashboard, Gavel, FileText, KeyRound, UserSearch, Upload, PackageX, User as UserIcon, TrendingUp, Send, Mail, Clock, Image as ImageIcon, Landmark, Copy, Check, Volume2, VolumeX, Truck, Store, Database } from "lucide-react";
import AdminContabilidad from "@/pages/AdminContabilidad";
import AdminPayouts from "@/pages/AdminPayouts";
import AdminChangelog from "@/pages/AdminChangelog";
import AdminAnalytics from "@/pages/AdminAnalytics";
import AdminPartnerStores from "@/pages/AdminPartnerStores";
import AdminConfigEditor from "@/pages/AdminConfigEditor";
import AdminSearchFieldsAudit from "@/pages/AdminSearchFieldsAudit";
import AdminSubastas from "@/pages/AdminSubastas";
import { GameTabs } from "@/components/GameTabs";
import { ReasonPickerDialog, BAN_REASONS, UNBAN_REASONS } from "@/components/ReasonPickerDialog";
import { isSealed, sealedLabel, sealedPillClass } from "@/lib/productType";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { variantsForCard } from "@/lib/gameConfig";
import { WaButton } from "@/components/WaButton";
import { UserInfoModal } from "@/components/UserInfoModal";
import { SellerShippingPanel } from "@/components/admin/SellerShippingPanel";
import { LedgerIntegrityPanel } from "@/components/admin/LedgerIntegrityPanel";
import { RatingsIntegrityPanel } from "@/components/admin/RatingsIntegrityPanel";
import { UserStrikesDialog } from "@/components/admin/UserStrikesDialog";
import { ManualRefundsPanel } from "@/components/admin/ManualRefundsPanel";
import { conditionOptions, isLotProduct } from "@/lib/conditions";
import { PossibleDuplicate, DraftPanel, NeedsInfoNote, RequestInfoButton, DuplicatesButton, RetryPublishButton } from "@/components/admin/CustomRequestReview";


async function uploadPublic(file) {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("scope", "public");
  fd.append("kind", "card");
  const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return r.data.path;
}

// 4 delimited sections — same treatment as buyer/seller panels.
//   PANEL       → dashboard/overview
//   MODERACIÓN  → identity + product + disputes (things to review/approve/reject)
//   CONTABILIDAD → money (orders + ledger docs, immutable)
//   CONFIGURACIÓN → users + rules + logs (system-level)
//
// Each item declares an optional `countKey` — the key of /admin/pending-counts
// whose value will be rendered as a red badge next to the link. Also `moderator`
// (default true) marks whether the item is visible to the moderator role;
// money and system-config items are set to `moderator: false`.
const NAV_SECTIONS = [
  {
    key: "panel", label: "Panel", icon: LayoutDashboard,
    items: [
      { to: "/admin", exact: true, label: "Resumen", icon: DollarSign },
      { to: "/admin/analytics", label: "Analytics", icon: TrendingUp, moderator: false },
    ],
  },
  {
    key: "moderation", label: "Moderación", icon: Gavel,
    items: [
      { to: "/admin/verifications", label: "Verificaciones", icon: Users, countKey: "verifications" },
      { to: "/admin/publications", label: "Publicaciones revisión", icon: ClipboardList, countKey: "publications_review" },
      { to: "/admin/publications-all", label: "Todas las publicaciones", icon: Layers },
      { to: "/admin/auctions", label: "Subastas revisión", icon: Gavel, countKey: "auctions_review" },
      { to: "/admin/custom-cards", label: "Cartas personalizadas", icon: Sparkles, countKey: "custom_cards" },
      { to: "/admin/cards-no-image", label: "Cartas sin imagen API", icon: ImageIcon, countKey: "cards_no_image" },
      { to: "/admin/disputes", label: "Disputas", icon: Scale, countKey: "disputes" },
      { to: "/admin/account-claims", label: "Reclamos de cuenta", icon: UserSearch, countKey: "account_claims" },
      { to: "/admin/refunds", label: "Reembolsos", icon: XCircle, countKey: "refunds" },
      { to: "/admin/manual-refunds", label: "Devoluciones manuales", icon: Send, countKey: "manual_refunds" },
      { to: "/admin/manual-refunds#liquidaciones", label: "Liquidaciones fallidas", icon: AlertTriangle, countKey: "settlements_failed" },
      { to: "/admin/seller-debts", label: "Deudas vendedores", icon: DollarSign, countKey: "seller_debts" },
      { to: "/admin/reports", label: "Reportes", icon: Flag, countKey: "reports" },
    ],
  },
  {
    key: "accounting", label: "Contabilidad", icon: FileText, moderator: false,
    items: [
      { to: "/admin/payouts", label: "Liberación de pagos", icon: DollarSign, countKey: "payouts_ready" },
      { to: "/admin/transfers", label: "Transferencias bancarias", icon: Landmark, countKey: "transfers_pending" },
      { to: "/admin/reconciliation", label: "Descuadres de pago", icon: AlertTriangle, countKey: "reconciliation_mismatches" },
      { to: "/admin/shipping-audit", label: "Auditoría de envíos", icon: Truck },
      { to: "/admin/orders", label: "Pedidos", icon: Package },
      { to: "/admin/duplicates", label: "Pedidos duplicados", icon: Copy, moderator: false },
      { to: "/admin/yugioh-seed", label: "Yu-Gi-Oh Seed", icon: Database, moderator: false },
      { to: "/admin/ledger", label: "Documentos / Ledger", icon: FileText },
      { to: "/admin/ledger-integrity", label: "Descuadres contables", icon: AlertTriangle, moderator: false },
      { to: "/admin/ratings-integrity", label: "Calificaciones no completadas", icon: AlertTriangle, moderator: false },
    ],
  },
  {
    key: "config", label: "Configuración", icon: Settings, moderator: false,
    items: [
      { to: "/admin/users", label: "Usuarios / Strikes", icon: ShieldAlert },
      { to: "/admin/seller-shipping", label: "Envíos de vendedores", icon: Truck },
      { to: "/admin/blacklist", label: "Lista negra", icon: Ban },
      { to: "/admin/audit", label: "Log de auditoría", icon: AlertTriangle },
      { to: "/admin/config", label: "Configuración", icon: Settings },
      { to: "/admin/search-audit", label: "Auditoría del buscador", icon: FileText, moderator: false },
      { to: "/admin/partner-stores", label: "Tiendas aliadas", icon: Store },
      { to: "/admin/reactivation", label: "Correo reactivación", icon: Mail },
      { to: "/admin/changelog", label: "Novedades / Changelog", icon: FileText, moderator: false },
      { to: "/admin/profile", label: "Mi perfil", icon: UserIcon, moderator: true },
      { to: "/admin/test-data", label: "Datos de prueba", icon: Trash2 },
    ],
  },
];

// Small red-circle badge with the count. Renders nothing when count is <= 0.
function CountBadge({ count, testid }) {
  if (!count || count <= 0) return null;
  return (
    <span
      data-testid={testid}
      className="ml-auto bg-[#EF4444] text-white text-[10px] font-bold min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center leading-none"
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const isModerator = user?.role === "moderator";
  const [counts, setCounts] = useState({});
  // Sonido de notificación cuando entra algo nuevo al panel (mientras el
  // admin está adentro). El admin puede silenciar con el toggle de arriba a
  // la derecha — se recuerda en localStorage.
  const [soundEnabled, setSoundEnabled] = useState(() => {
    try { return localStorage.getItem("hittcg_admin_sound") !== "off"; } catch { return true; }
  });
  const prevTotalRef = useRef(null);
  const audioCtxRef = useRef(null);
  const firstLoadRef = useRef(true);

  const playPing = () => {
    // Chime generado en Web Audio API — dos tonos suaves (E5 → G5). Sin
    // asset externo. Se crea AudioContext lazily en la primera reproducción
    // porque los navegadores bloquean AudioContext hasta el primer gesto.
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      if (!audioCtxRef.current) audioCtxRef.current = new AC();
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") ctx.resume();
      const now = ctx.currentTime;
      const tones = [
        { f: 659.25, t: 0 },     // E5
        { f: 783.99, t: 0.15 },  // G5
      ];
      tones.forEach(({ f, t }) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = f;
        gain.gain.setValueAtTime(0.0001, now + t);
        gain.gain.exponentialRampToValueAtTime(0.18, now + t + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + t + 0.35);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now + t);
        osc.stop(now + t + 0.4);
      });
    } catch (e) {
      // Silencio en fallo — el ping es opcional, no bloqueante.
    }
  };

  const toggleSound = () => {
    setSoundEnabled((v) => {
      const next = !v;
      try { localStorage.setItem("hittcg_admin_sound", next ? "on" : "off"); } catch { /* noop */ }
      if (next) playPing(); // pre-arma el AudioContext + feedback audible
      return next;
    });
  };

  // Filter the nav based on role: moderator sees Panel + Moderación + Mi perfil only.
  const nav = NAV_SECTIONS
    .map(section => {
      if (isModerator && section.moderator === false) {
        // Keep the section only if it has any explicitly-allowed items (e.g. Mi perfil).
        const allowed = section.items.filter(it => it.moderator === true);
        if (allowed.length === 0) return null;
        return { ...section, items: allowed };
      }
      return section;
    })
    .filter(Boolean);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const r = await api.get("/admin/pending-counts");
        if (!mounted) return;
        const next = r.data || {};
        // Ping al admin cuando el total sube — comparamos con el snapshot
        // anterior. El primer load NO suena (sino sonaría cada vez que abre
        // el panel).
        const prev = prevTotalRef.current;
        const nextTotal = Number(next.total || 0);
        if (!firstLoadRef.current && prev !== null && nextTotal > prev && soundEnabled) {
          playPing();
          try {
            toast.success(`Nueva actividad en el panel (+${nextTotal - prev})`, { duration: 3500 });
          } catch { /* noop */ }
        }
        prevTotalRef.current = nextTotal;
        firstLoadRef.current = false;
        setCounts(next);
      } catch { /* ignore transient errors — badges just won't update */ }
    };
    load();
    // Polling cada 20s mientras el admin está en el panel — antes 45s se
    // sentía "muerto". 20s es suficiente para percibir la actividad sin
    // saturar el backend.
    const t = setInterval(load, 20000);
    return () => { mounted = false; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [soundEnabled]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-2xl sm:text-3xl md:text-4xl">{isModerator ? "PANEL MODERADOR" : "PANEL ADMIN"}</h1>
          <p className="text-sm text-neutral-500 mt-1">
            {isModerator
              ? "Acceso a moderación logística: verificaciones, publicaciones, disputas, reportes y reclamos. Sin acceso a dinero, configuración ni gestión de usuarios."
              : "Panel (dashboard) · Moderación (revisar/aprobar) · Contabilidad (dinero y documentos) · Configuración (sistema)."}
          </p>
          {counts.total > 0 && (
            <div className="mt-3 inline-flex items-center gap-2 bg-[#EF4444]/10 border border-[#EF4444]/40 rounded-full px-3 py-1 text-xs text-[#EF4444]" data-testid="admin-total-pending">
              <TrendingUp size={12} /> <b>{counts.total}</b> ítems pendientes de acción
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={toggleSound}
          data-testid="admin-sound-toggle"
          title={soundEnabled ? "Silenciar notificaciones sonoras" : "Activar notificaciones sonoras"}
          className={`shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-md border text-xs transition ${soundEnabled ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/20" : "bg-[#111] border-[#262626] text-neutral-500 hover:text-white"}`}
        >
          {soundEnabled ? <Volume2 size={14} /> : <VolumeX size={14} />}
          <span className="hidden sm:inline">{soundEnabled ? "Sonido ON" : "Silencio"}</span>
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-6">
        <aside className="bg-[#111] border border-[#262626] rounded-md p-2 h-fit md:sticky md:top-24" data-testid="admin-sidebar">
          <nav className="flex flex-col gap-4">
            {nav.map(section => (
              <div key={section.key} data-testid={`admin-section-${section.key}`}>
                <div className="flex items-center gap-2 px-3 pb-1 text-[10px] uppercase tracking-widest text-neutral-500 border-b border-[#262626] mb-1">
                  <section.icon size={11} />{section.label}
                </div>
                <div className="flex flex-col gap-0.5">
                  {section.items.map(n => (
                    <NavLink
                      key={n.to}
                      to={n.to}
                      end={n.exact}
                      data-testid={`admin-nav-${n.to.replace("/admin/", "").replace("/admin", "overview")}`}
                      className={({ isActive }) =>
                        `flex items-center gap-2 px-3 py-1.5 rounded-md text-sm ${isActive ? "bg-[#EF4444] text-white" : "text-neutral-400 hover:text-white hover:bg-[#171717]"}`
                      }
                    >
                      <n.icon size={13} /> <span className="truncate">{n.label}</span>
                      {n.countKey && (
                        <CountBadge count={counts[n.countKey]} testid={`badge-${n.countKey}`} />
                      )}
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
          </nav>
        </aside>
        <div>
          <Routes>
            <Route index element={<Overview />} />
            <Route path="verifications" element={<Verifications />} />
            {!isModerator && <Route path="payouts" element={<AdminPayouts />} />}
            {!isModerator && <Route path="transfers" element={<AdminTransfers />} />}
            <Route path="orders" element={<AllOrders />} />
            {!isModerator && <Route path="duplicates" element={<Duplicates />} />}
            {!isModerator && <Route path="yugioh-seed" element={<YugiohSeed />} />}
            {!isModerator && <Route path="ledger" element={<AdminContabilidad />} />}
            <Route path="reconciliation" element={<Reconciliation />} />
            <Route path="shipping-audit" element={<ShippingAudit />} />
            <Route path="disputes" element={<Disputes />} />
            <Route path="security" element={<Navigate to="/admin/account-claims" replace />} />
            <Route path="account-claims" element={<AccountClaims />} />
            <Route path="refunds" element={<RefundRequests />} />
            <Route path="manual-refunds" element={<ManualRefundsPanel />} />
            <Route path="seller-debts" element={<SellerDebts />} />
            <Route path="publications" element={<PubReview />} />
            <Route path="publications-all" element={<PublicationsAll />} />
            <Route path="auctions" element={<AdminSubastas />} />
            <Route path="custom-cards" element={<CustomCards />} />
            <Route path="cards-no-image" element={<CardsNoImage />} />
            <Route path="reports" element={<Reports />} />
            {!isModerator && <Route path="users" element={<UsersList />} />}
            {!isModerator && <Route path="seller-shipping" element={<SellerShippingPanel />} />}
            {!isModerator && <Route path="ledger-integrity" element={<LedgerIntegrityPanel />} />}
            {!isModerator && <Route path="ratings-integrity" element={<RatingsIntegrityPanel />} />}
            {!isModerator && <Route path="blacklist" element={<Blacklist />} />}
            {!isModerator && <Route path="audit" element={<AuditLog />} />}
            {!isModerator && <Route path="config" element={<><AdminConfigEditor /><Config /></>} />}
            {!isModerator && <Route path="search-audit" element={<AdminSearchFieldsAudit />} />}
            {!isModerator && <Route path="partner-stores" element={<AdminPartnerStores />} />}
            {!isModerator && <Route path="reactivation" element={<Reactivation />} />}
            {!isModerator && <Route path="changelog" element={<AdminChangelog />} />}
            <Route path="profile" element={<AdminProfile />} />
            {!isModerator && <Route path="test-data" element={<TestData />} />}
            {!isModerator && <Route path="analytics" element={<AdminAnalytics />} />}
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children, actions }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl">{title}</h2>
        {actions}
      </div>
      {children}
    </div>
  );
}

function SecurityTickets_DEPRECATED() {
  // Old flow removed — admins now use /admin/account-claims. Kept as placeholder
  // to avoid breaking imports; component is no longer rendered from the router.
  return null;
}

function AccountClaims() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState("");
  const [reason, setReason] = useState("");
  const { confirm, prompt } = useDialog();
  const { open } = useLightbox();

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/account-claims", { params: filter === "all" ? {} : { status_filter: filter } });
      setList(r.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const openClaim = async (claimId) => {
    setSelected(claimId);
    setDetail(null);
    setNotes(""); setReason("");
    try {
      const r = await api.get(`/admin/account-claims/${claimId}`);
      setDetail(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo cargar el reclamo");
      setSelected(null);
    }
  };

  const approveIdentity = async () => {
    if (!detail) return;
    const ok = await confirm({
      title: "Aprobar identidad",
      message: `Las fotos coinciden. Se enviará un código OTP a ${detail.claim.contact_email}. La cuenta seguirá bloqueada hasta que el usuario complete el flujo.`,
      confirmText: "Aprobar identidad",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.post(`/admin/account-claims/${detail.claim.id}/approve-identity`, { notes: notes || null });
      toast.success("Identidad aprobada. OTP enviado al reclamante.");
      await load();
      await openClaim(detail.claim.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo aprobar");
    } finally { setBusy(false); }
  };

  const rejectClaim = async () => {
    if (!detail) return;
    let r = reason.trim();
    if (!r) {
      r = (await prompt({ title: "Rechazar reclamo", label: "Motivo (obligatorio, se registra en auditoría)", multiline: true, required: true })) || "";
      r = r.trim();
    }
    if (r.length < 3) {
      toast.error("El motivo debe tener al menos 3 caracteres");
      return;
    }
    const ok = await confirm({
      title: "Rechazar reclamo",
      message: "La cuenta permanecerá bloqueada. El reclamante no recibirá el enlace de nueva contraseña.",
      confirmText: "Rechazar", danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.post(`/admin/account-claims/${detail.claim.id}/reject`, { reason: r });
      toast.success("Reclamo rechazado.");
      await load();
      await openClaim(detail.claim.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo rechazar");
    } finally { setBusy(false); }
  };

  const statusBadge = (s) => {
    const map = {
      pending: ["bg-amber-500/20 text-amber-300 border-amber-500/40", "PENDIENTE"],
      identity_approved: ["bg-blue-500/20 text-blue-300 border-blue-500/40", "IDENTIDAD OK · OTP ENVIADO"],
      email_verified: ["bg-emerald-500/20 text-emerald-300 border-emerald-500/40", "CORREO VERIFICADO · ESPERA COMPLETE"],
      completed: ["bg-emerald-500/20 text-emerald-300 border-emerald-500/40", "COMPLETADO"],
      rejected: ["bg-red-500/20 text-red-300 border-red-500/40", "RECHAZADO"],
    };
    const [cls, label] = map[s] || ["bg-neutral-500/20 text-neutral-300 border-neutral-500/40", s];
    return <span className={`text-[10px] px-2 py-0.5 rounded border ${cls}`}>{label}</span>;
  };

  if (selected && detail) {
    const c = detail.claim; const u = detail.original_user;
    return (
      <Section title="Reclamo — Verificación de identidad" actions={
        <Button variant="outline" size="sm" data-testid="claim-back-list" onClick={() => { setSelected(null); setDetail(null); }} className="border-[#262626]">
          <X size={14} className="mr-1" /> Volver a la lista
        </Button>
      }>
        <div className="space-y-6">
          <div className="bg-[#111] border border-[#262626] rounded-md p-4">
            <div className="flex items-center gap-3 flex-wrap mb-3">
              {statusBadge(c.status)}
              <span className="text-xs text-neutral-500 font-mono">{c.id.slice(0, 8)}</span>
              <span className="text-xs text-neutral-500">Enviado: {new Date(c.submitted_at).toLocaleString("es-EC")}</span>
              <span className="text-xs text-neutral-500">IP: {c.submitted_ip || "—"}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-neutral-500 uppercase text-xs">Cuenta original</div>
                {u ? (
                  <div className="mt-1 space-y-0.5">
                    <div>Nombre: <span className="text-white">{u.name || "—"}</span></div>
                    <div>Cédula: <span className="text-white font-mono">{u.cedula || "—"}</span></div>
                    <div>Email actual: <span className="text-white">{u.email}</span></div>
                    <div>Estado: <span className={u.banned ? "text-red-400" : "text-emerald-400"}>{u.banned ? "BLOQUEADA" : "ACTIVA"}</span></div>
                  </div>
                ) : (
                  <div className="text-red-400 mt-1">No hay cuenta original que coincida. Solo se puede rechazar este reclamo.</div>
                )}
              </div>
              <div>
                <div className="text-neutral-500 uppercase text-xs">Correo de contacto propuesto</div>
                <div className="mt-1 text-white font-mono">{c.contact_email}</div>
                {u && c.contact_email !== u.email && (
                  <div className="text-xs text-amber-400 mt-1">⚠ Distinto al correo original. Se actualizará al completar el flujo.</div>
                )}
              </div>
            </div>
          </div>

          {/* Side-by-side comparison */}
          <div>
            <h3 className="font-display text-xl mb-3">Comparación de fotos</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <PhotoCompareColumn
                title="ORIGINALES (registro)"
                pairs={u ? [
                  ["Selfie original", u.selfie_photo_path],
                  ["Cédula frente original", u.cedula_photo_path],
                  ["Cédula reverso original", u.cedula_back_photo_path],
                ] : []}
                empty={u ? "Este usuario nunca completó verificación de identidad — no hay fotos originales para comparar" : "Cuenta original no encontrada"}
                onOpen={open}
              />
              <PhotoCompareColumn
                title="NUEVAS (reclamo)"
                pairs={[
                  ["Selfie nuevo", c.new_selfie_path],
                  ["Cédula frente nueva", c.new_cedula_front_path],
                  ["Cédula reverso nueva", c.new_cedula_back_path],
                ]}
                onOpen={open}
              />
            </div>
          </div>

          {c.status === "pending" && (
            <div className="bg-[#111] border border-[#262626] rounded-md p-4 space-y-3">
              <div>
                <Label className="text-xs text-neutral-400 mb-1 block">Notas (opcional, para el ticket)</Label>
                <Input
                  data-testid="claim-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="bg-[#171717] border-[#262626] text-white"
                  placeholder="Ej: Cédula #123 coincide, cara idéntica"
                />
              </div>
              <div>
                <Label className="text-xs text-neutral-400 mb-1 block">Motivo de rechazo (obligatorio si rechazas)</Label>
                <Input
                  data-testid="claim-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="bg-[#171717] border-[#262626] text-white"
                  placeholder="Ej: No hay cuenta que coincida · fotos borrosas · impostor"
                />
              </div>
              <div className="flex gap-3 flex-wrap">
                {u && (
                  <Button
                    data-testid="claim-approve"
                    onClick={approveIdentity}
                    disabled={busy}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    Aprobar identidad y enviar OTP
                  </Button>
                )}
                <Button
                  data-testid="claim-reject"
                  onClick={rejectClaim}
                  disabled={busy}
                  variant="outline"
                  className="border-red-500 text-red-400 hover:bg-red-500/10"
                >
                  Rechazar reclamo
                </Button>
                {!u && (
                  <div className="text-xs text-amber-400 self-center">
                    Sin cuenta original que coincida → solo se puede rechazar.
                  </div>
                )}
              </div>
            </div>
          )}

          {c.status !== "pending" && (
            <div className="bg-[#111] border border-[#262626] rounded-md p-4 text-sm space-y-1">
              {c.admin_notes && <div>Notas admin: <span className="text-white">{c.admin_notes}</span></div>}
              {c.rejection_reason && <div>Motivo rechazo: <span className="text-red-300">{c.rejection_reason}</span></div>}
              {c.reviewed_at && <div>Revisado: {new Date(c.reviewed_at).toLocaleString("es-EC")}</div>}
              {c.completed_at && <div>Completado: {new Date(c.completed_at).toLocaleString("es-EC")}</div>}
              {c.status === "identity_approved" && (
                <div className="text-blue-300">Esperando que el reclamante verifique el OTP en su nuevo correo.</div>
              )}
              {c.status === "email_verified" && (
                <div className="text-emerald-300">Correo verificado. Esperando que el reclamante use el enlace mágico (24h).</div>
              )}
              {c.status !== "completed" && c.status !== "rejected" && u && (
                <div className="pt-2">
                  <Button
                    data-testid="claim-reject-late"
                    onClick={rejectClaim}
                    variant="outline"
                    size="sm"
                    className="border-red-500 text-red-400"
                  >
                    Rechazar / abortar flujo
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </Section>
    );
  }

  return (
    <Section title="Reclamos de cuenta (recuperación por fotos)" actions={
      <div className="flex gap-2 text-xs flex-wrap">
        {[["pending", "Pendientes"], ["identity_approved", "OTP"], ["email_verified", "Enlace"], ["completed", "Completados"], ["rejected", "Rechazados"], ["all", "Todos"]].map(([f, l]) => (
          <button key={f} data-testid={`claim-filter-${f}`} onClick={() => setFilter(f)}
            className={`px-3 py-1 rounded-md border ${filter === f ? "bg-[#EF4444] border-[#EF4444] text-white" : "border-[#262626] text-neutral-400 hover:text-white"}`}>
            {l}
          </button>
        ))}
      </div>
    }>
      {loading ? <div className="text-neutral-500">Cargando…</div> :
        list.length === 0 ? <div className="border border-dashed border-[#262626] rounded-md p-8 text-center text-neutral-500">Sin reclamos.</div> :
        <div className="space-y-3" data-testid="claim-list">
          {list.map((c) => (
            <button
              key={c.id}
              data-testid={`claim-row-${c.id}`}
              onClick={() => openClaim(c.id)}
              className="w-full text-left bg-[#111] border border-[#262626] rounded-md p-4 hover:border-[#EF4444] transition"
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {statusBadge(c.status)}
                    <span className="text-xs font-mono text-neutral-500">{c.id.slice(0, 8)}</span>
                  </div>
                  <div className="text-sm text-neutral-300 mt-2">
                    Contacto: <span className="text-white font-mono">{c.contact_email}</span>
                  </div>
                  <div className="text-xs text-neutral-500">
                    Original: {c.original_email_snapshot || <span className="text-red-400">sin coincidencia</span>} · {new Date(c.submitted_at).toLocaleString("es-EC")}
                  </div>
                </div>
                <div className="text-xs text-neutral-500">Abrir →</div>
              </div>
            </button>
          ))}
        </div>
      }
    </Section>
  );
}

function PhotoCompareColumn({ title, pairs, empty, onOpen }) {
  const validPairs = pairs.filter(([, p]) => !!p);
  return (
    <div className="bg-[#0F0F0F] border border-[#262626] rounded-md p-4">
      <h4 className="font-display text-sm uppercase tracking-wider text-neutral-400 mb-3">{title}</h4>
      {validPairs.length === 0 ? (
        <div className="text-xs text-red-300 py-8 text-center">{empty || "Sin fotos"}</div>
      ) : (
        <div className="space-y-3">
          {validPairs.map(([label, path]) => (
            <div key={path}>
              <div className="text-xs text-neutral-500 mb-1">{label}</div>
              <button
                type="button"
                onClick={() => onOpen(path)}
                data-testid={`claim-img-${label.replace(/\s+/g, "-").toLowerCase()}`}
                className="block w-full"
              >
                <AuthImg path={path} width={600} className="w-full max-h-80 object-contain bg-black rounded border border-[#262626] hover:border-[#EF4444] transition" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Overview() {
  const { user } = useAuth();
  const isModerator = user?.role === "moderator";
  const [money, setMoney] = useState(null);
  const [orders, setOrders] = useState([]);
  const [demo, setDemo] = useState([]);
  const [counts, setCounts] = useState({});
  useEffect(() => {
    // Everyone (admin + moderator) sees the pending-counts summary.
    api.get("/admin/pending-counts").then(r => setCounts(r.data || {})).catch(() => {});
    // Money/demo-orders are super-admin only. Skip them for moderators to avoid 403.
    if (isModerator) return;
    api.get("/admin/money").then(r => setMoney(r.data)).catch(() => {});
    api.get("/admin/orders").then(r => setOrders(r.data)).catch(() => {});
    // Auto-reseed demo orders each time admin lands on the dashboard
    api.post("/admin/demo-orders/reseed").then(r => setDemo(r.data.orders)).catch(() => {
      api.get("/admin/demo-orders").then(r => setDemo(r.data)).catch(() => {});
    });
  }, [isModerator]);
  const STATUS_COLOR = {
    PENDIENTE_PAGO: "text-neutral-400", PAGADO: "text-[#F87171]",
    ENVIADO: "text-blue-400", ENTREGADO: "text-amber-400", PENDIENTE_LIBERACION: "text-amber-400",
    LIBERADO: "text-emerald-400", EN_DISPUTA: "text-[#FCA5A5]",
    REEMBOLSADO: "text-red-300", CANCELADO: "text-neutral-400",
  };
  // Moderator sees ONLY the logistics queue summary — no money, no orders table.
  if (isModerator) {
    const QUEUES = [
      { key: "verifications", label: "Verificaciones pendientes", to: "/admin/verifications" },
      { key: "verification_stores", label: "Verificación de tiendas", to: "/admin/verifications" },
      { key: "publications_review", label: "Publicaciones por revisar", to: "/admin/publications" },
      { key: "custom_cards", label: "Cartas personalizadas", to: "/admin/custom-cards" },
      { key: "disputes", label: "Disputas abiertas", to: "/admin/disputes" },
      { key: "account_claims", label: "Reclamos de cuenta", to: "/admin/account-claims" },
      { key: "refunds", label: "Solicitudes de reembolso", to: "/admin/refunds" },
      { key: "reports", label: "Reportes abiertos", to: "/admin/reports" },
    ];
    return (
      <Section title="Colas de moderación">
        <div className="p-3 bg-blue-500/10 border border-blue-500/40 rounded text-xs text-blue-200 mb-4">
          <b>Cuenta de moderador.</b> Puedes atender colas logísticas (verificaciones, publicaciones, disputas, reportes y reclamos). No tienes acceso a dinero, configuración ni gestión de usuarios.
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="mod-queues-grid">
          {QUEUES.map(q => (
            <NavLink
              key={q.key}
              to={q.to}
              data-testid={`mod-queue-${q.key}`}
              className={`bg-[#111] border rounded-md p-4 hover:border-[#EF4444] transition-colors ${counts[q.key] > 0 ? "border-[#EF4444]/50" : "border-[#262626]"}`}
            >
              <div className="text-neutral-500 text-xs uppercase tracking-wider">{q.label}</div>
              <div className={`font-display text-3xl mt-2 ${counts[q.key] > 0 ? "text-[#EF4444]" : "text-neutral-600"}`}>
                {counts[q.key] || 0}
              </div>
            </NavLink>
          ))}
        </div>
      </Section>
    );
  }
  return (
    <Section title="Resumen financiero">
      {money && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6" data-testid="money-cards">
          <Card label="Retenido a vendedores" value={`$${money.held_for_sellers.toFixed(2)}`} />
          <Card label="Comisión ganada" value={`$${money.commission_earned.toFixed(2)}`} highlight />
          <Card label="Bruto procesado" value={`$${money.gross_processed.toFixed(2)}`} />
          <Card label="Reembolsos" value={`$${money.refunds_processed.toFixed(2)}`} />
          <Card label="Interbancario cobrado a vendedores" value={`$${(money.interbank_collected || 0).toFixed(2)}`} />
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-center text-sm mb-8">
        {["PAGADO", "ENVIADO", "ENTREGADO", "EN_DISPUTA", "LIBERADO"].map(s => (
          <div key={s} className="bg-[#111] border border-[#262626] rounded-md p-3">
            <div className="text-neutral-500 text-xs uppercase">{s}</div>
            <div className="font-display text-2xl mt-1">{orders.filter(o => o.status === s).length}</div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-xl">Pedidos DEMO (uno por estado)</h3>
        <Button
          data-testid="reseed-demo-orders"
          size="sm"
          variant="outline"
          className="border-[#EF4444] text-[#EF4444]"
          onClick={() => api.post("/admin/demo-orders/reseed").then(r => { setDemo(r.data.orders); toast.success("Pedidos demo reiniciados"); })}
        >
          Reiniciar demo
        </Button>
      </div>
      <div className="text-xs text-neutral-500 mb-3">Se reinician automáticamente cada vez que entras al panel. Como admin puedes verlos &quot;como comprador&quot; o &quot;como vendedor&quot;.</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {demo.map(o => (
          <div key={o.id} data-testid={`demo-order-${o.status}`} className="bg-[#111] border border-[#262626] rounded-md p-3">
            <div className="flex items-center justify-between mb-2">
              <span className={`font-display text-lg ${STATUS_COLOR[o.status] || "text-white"}`}>{o.status}</span>
              <span className="text-[10px] font-mono text-neutral-500">{o.id?.slice(0, 8)}</span>
            </div>
            <div className="text-sm text-neutral-400 mb-2">{o.card_name}</div>
            <div className="flex gap-2">
              <a href={`/order/${o.id}?view=buyer`} className="flex-1"><Button data-testid={`demo-view-buyer-${o.status}`} size="sm" variant="outline" className="w-full border-blue-500/50 text-blue-400">Ver como comprador</Button></a>
              <a href={`/order/${o.id}?view=seller`} className="flex-1"><Button data-testid={`demo-view-seller-${o.status}`} size="sm" variant="outline" className="w-full border-emerald-500/50 text-emerald-400">Ver como vendedor</Button></a>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function Card({ label, value, highlight }) {
  return (
    <div className={`bg-[#111] border rounded-md p-4 stat-bar ${highlight ? "border-[#EF4444]" : "border-[#262626]"}`}>
      <div className="text-neutral-500 text-xs uppercase tracking-wider">{label}</div>
      <div className="font-display text-2xl mt-2">{value}</div>
    </div>
  );
}

function VerifDocPreview({ path, testid, openImg }) {
  if (!path) return <div className="text-xs text-neutral-600">Sin subir</div>;
  const isPdf = /\.pdf$/i.test(path);
  if (isPdf) {
    const token = localStorage.getItem("hittcg_token") || "";
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/private/${path}?auth=${encodeURIComponent(token)}`;
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={testid}
        className="w-full h-32 flex flex-col items-center justify-center bg-red-500/5 border border-red-500/30 rounded text-red-300 hover:bg-red-500/10 hover:border-[#EF4444] transition"
      >
        <div className="font-mono font-bold text-2xl">PDF</div>
        <div className="text-[10px] mt-1">Abrir en pestaña nueva</div>
      </a>
    );
  }
  return (
    <button type="button" onClick={() => openImg([{ path, scope: "private" }])} data-testid={testid}>
      <AuthImg path={path} width={400} className="w-full h-32 object-contain bg-[#0A0A0A] rounded border border-[#262626] cursor-pointer hover:border-[#EF4444]" />
    </button>
  );
}

function Verifications() {
  const [list, setList] = useState([]);
  const { open } = useLightbox();
  const { prompt } = useDialog();
  const load = () => api.get("/admin/verifications").then(r => setList(r.data));
  useEffect(() => { load(); }, []);
  const approve = async (id) => { await api.post(`/admin/verifications/${id}/approve`); toast.success("Aprobado"); load(); };
  const reject = async (id) => {
    const r = await prompt({ title: "Rechazar verificación", label: "Motivo (el usuario lo verá)", multiline: true, required: true });
    if (!r) return;
    await api.post(`/admin/verifications/${id}/reject?reason=${encodeURIComponent(r)}`); toast.success("Rechazado"); load();
  };
  return (
    <Section title={`Verificaciones pendientes (${list.length})`}>
      {list.length === 0 && <div className="text-neutral-500">Sin cola.</div>}
      <div className="space-y-4">
        {list.map(u => (
          <div key={u.id} className="bg-[#111] border border-[#262626] rounded-md p-4" data-testid={`verif-${u.id}`}>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <div className="font-medium">{u.name}</div>
                <div className="text-sm text-neutral-500">{u.email}</div>
                <div className="text-sm text-neutral-500">Cédula: {u.cedula}</div>
                <div className="text-sm text-neutral-500">Ciudad: {u.city}</div>
                <div className="text-sm text-neutral-500">Nace: {u.birthdate}</div>
              </div>
              <div>
                <div className="text-xs text-neutral-500 mb-1">Tipo de cuenta</div>
                <div className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded inline-block ${u.account_type === "store" ? "bg-purple-500/20 text-purple-300" : "bg-neutral-500/20 text-neutral-300"}`}>
                  {u.account_type === "store" ? `Tienda${u.store_name ? ` · ${u.store_name}` : ""}` : "Persona"}
                </div>
                {u.account_type === "store" && u.ruc && <div className="text-xs text-neutral-500 mt-1 font-mono">RUC: {u.ruc}</div>}
              </div>
              {u.account_type === "store" && (
                <>
                  <div>
                    <div className="text-xs text-neutral-500 mb-1">Documento RUC (SRI)</div>
                    <VerifDocPreview path={u.ruc_document_path} testid={`verif-ruc-${u.id}`} openImg={open} />
                  </div>
                  <div>
                    <div className="text-xs text-neutral-500 mb-1">Certificado bancario</div>
                    <VerifDocPreview path={u.bank_cert_photo_path} testid={`verif-bank-${u.id}`} openImg={open} />
                  </div>
                </>
              )}
              <div>
                <div className="text-xs text-neutral-500 mb-1">Cédula (frente)</div>
                {u.cedula_photo_path ? (
                  <button type="button" onClick={() => open([{ path: u.cedula_photo_path, scope: "private" }])} data-testid={`verif-cedula-${u.id}`}>
                    <AuthImg path={u.cedula_photo_path} width={500} className="w-full h-40 object-contain bg-[#0A0A0A] rounded border border-[#262626] cursor-pointer hover:border-[#EF4444]" />
                  </button>
                ) : <div className="text-xs text-neutral-600">Sin subir</div>}
              </div>
              <div>
                <div className="text-xs text-neutral-500 mb-1">Cédula (reverso)</div>
                {u.cedula_back_photo_path ? (
                  <button type="button" onClick={() => open([{ path: u.cedula_back_photo_path, scope: "private" }])} data-testid={`verif-cedula-back-${u.id}`}>
                    <AuthImg path={u.cedula_back_photo_path} width={500} className="w-full h-40 object-contain bg-[#0A0A0A] rounded border border-[#262626] cursor-pointer hover:border-[#EF4444]" />
                  </button>
                ) : <div className="text-xs text-neutral-600">Sin subir</div>}
              </div>
              <div>
                <div className="text-xs text-neutral-500 mb-1">Selfie</div>
                {u.selfie_photo_path ? (
                  <button type="button" onClick={() => open([{ path: u.selfie_photo_path, scope: "private" }])} data-testid={`verif-selfie-${u.id}`}>
                    <AuthImg path={u.selfie_photo_path} width={500} className="w-full h-40 object-contain bg-[#0A0A0A] rounded border border-[#262626] cursor-pointer hover:border-[#EF4444]" />
                  </button>
                ) : <div className="text-xs text-neutral-600">Sin subir</div>}
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button data-testid={`verif-approve-${u.id}`} onClick={() => approve(u.id)} className="bg-emerald-600 hover:bg-emerald-700">Aprobar</Button>
              <Button data-testid={`verif-reject-${u.id}`} onClick={() => reject(u.id)} variant="outline" className="border-[#EF4444] text-[#EF4444]">Rechazar</Button>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function PublicationsAll() {
  const [list, setList] = useState([]);
  const [status, setStatus] = useState("");
  const [game, setGame] = useState("all"); // Fase 3 admin
  const [editing, setEditing] = useState(null);
  const { confirm } = useDialog();
  const load = () => api.get("/admin/publications-all", {
    params: { ...(status ? { status } : {}), ...(game !== "all" ? { game } : {}) },
  }).then(r => setList(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, game]);
  const remove = async (id) => {
    const ok = await confirm({ title: "Dar de baja", description: "La publicación se ocultará permanentemente.", destructive: true, confirmText: "Dar de baja" });
    if (!ok) return;
    await api.delete(`/admin/publications/${id}/force`);
    toast.success("Retirada"); load();
  };
  const save = async () => {
    if (!editing) return;
    const sealed = isSealed(editing);
    const body = {
      language: editing.language,
      price: parseFloat(editing.price),
      quantity: parseInt(editing.quantity),
      ship_cost: parseFloat(editing.ship_cost || 0),
    };
    if (!sealed) {
      body.condition = editing.condition;
      body.variant = editing.variant;
    }
    try {
      await api.put(`/admin/publications/${editing.id}`, body);
      toast.success("Publicación editada"); setEditing(null); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo guardar");
    }
  };
  const sweepGhosts = async () => {
    try {
      const r = await api.post("/admin/catalog/cleanup-ghost-variants");
      const n = r.data?.cleaned_count || 0;
      toast.success(n > 0
        ? `Limpieza OK: ${n} tiles fantasma removidos del catálogo`
        : "Catálogo limpio — no había fantasmas");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo limpiar");
    }
  };
  const sweepSealed = async () => {
    try {
      const r = await api.post("/admin/catalog/repair-sealed-flags");
      const n = r.data?.fixed_count || 0;
      toast.success(n > 0
        ? `Reparación OK: ${n} productos ahora marcados como sellado`
        : "Todo el catálogo tiene los flags de sellado correctos");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo reparar");
    }
  };
  const sweepReviewLoop = async () => {
    try {
      const r = await api.post("/admin/publications/repair-review-loop");
      const n = r.data?.restored_count || 0;
      toast.success(n > 0
        ? `Restauradas ${n} publicaciones que quedaron atrapadas en revisión por editar stock/precio`
        : "No hay publicaciones atrapadas por edición trivial");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo restaurar");
    }
  };
  const sweepGameSlug = async () => {
    try {
      const r = await api.post("/admin/repair/game-slug-crossed");
      const n = r.data?.total_fixed || 0;
      const rb = r.data?.moved_to_riftbound || 0;
      const pk = r.data?.moved_to_pokemon || 0;
      toast.success(n > 0
        ? `Corregidas ${n} cartas (${rb} → Riftbound, ${pk} → Pokémon). El catálogo se actualiza en segundos.`
        : "El catálogo ya está limpio — ninguna carta con game_slug cruzado");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo reparar");
    }
  };
  return (
    <Section title={`Todas las publicaciones (${list.length})`} actions={
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={sweepReviewLoop}
          data-testid="pub-all-repair-review-loop"
          className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 text-xs"
          title="Restaura publicaciones que quedaron en revisión por editar solo precio/stock/envío (bug del 22-Ago)"
        >
          Restaurar editadas
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={sweepSealed}
          data-testid="pub-all-repair-sealed"
          className="border-purple-500/40 text-purple-300 hover:bg-purple-500/10 text-xs"
          title="Marca correctamente como 'sellado' productos que quedaron sin el flag (bug histórico)"
        >
          Reparar sellados
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={sweepGhosts}
          data-testid="pub-all-sweep-ghosts"
          className="border-purple-500/40 text-purple-300 hover:bg-purple-500/10 text-xs"
          title="Elimina tiles 'Agotado' huérfanos del catálogo (snapshots sin publicación ni órdenes)"
        >
          Limpiar fantasmas
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={sweepGameSlug}
          data-testid="pub-all-sweep-game-slug"
          className="border-blue-500/40 text-blue-300 hover:bg-blue-500/10 text-xs"
          title="Mueve al juego correcto las cartas cuyo game_slug contradice el formato del ID (ej. base1-4 marcado como riftbound)"
        >
          Reparar juegos cruzados
        </Button>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="bg-[#171717] border border-[#262626] rounded px-3 py-1 text-sm">
          <option value="">Todos</option>
          {["active", "pending_review", "rejected", "sold", "removed"].map(s => <option key={s}>{s}</option>)}
        </select>
      </div>
    }>
      <GameTabs value={game} onChange={setGame} testidPrefix="admin-pubs-all" className="mb-3" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {list.map(p => {
          const sealed = isSealed(p);
          return (
          <div key={p.id} className={`bg-[#111] border rounded-md p-3 flex gap-3 border-l-2 ${sealed ? "border-purple-500/60 border-l-purple-500" : "border-[#262626] border-l-transparent"}`}>
            <div className={`w-16 h-20 bg-[#0A0A0A] rounded overflow-hidden shrink-0 border ${sealed ? "border-purple-500/60" : "border-transparent"}`}>
              {p.card?.image_url && <img src={cardImageUrl(p.card.image_url)} alt="" className="w-full h-full object-contain" onError={(e) => e.currentTarget.style.display = "none"} />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <div className="text-sm line-clamp-1 flex-1 min-w-0">{cardName(p.card, p.language)}</div>
                {sealed && <span className={sealedPillClass} data-testid={`pub-all-sealed-${p.id}`}>📦 {sealedLabel(p)}</span>}
              </div>
              <div className="text-xs text-neutral-500">{p.language.toUpperCase()} · {p.variant || "normal"} · {p.condition} · ${p.price}</div>
              <div className="text-xs text-neutral-500">Vendedor: {p.seller?.name}</div>
              <div className="text-[10px] mt-1"><span className={`px-1.5 py-0.5 rounded ${p.status === "active" ? "bg-emerald-500/20 text-emerald-400" : p.status === "sold" ? "bg-neutral-500/20 text-neutral-400" : "bg-amber-500/20 text-amber-400"}`}>{p.status}</span></div>
            </div>
            <div className="flex flex-col gap-1 shrink-0">
              {p.status !== "removed" && p.status !== "sold" && (
                <Button size="sm" variant="ghost" onClick={() => setEditing({ ...p })} className="text-amber-400 hover:bg-amber-500/10" data-testid={`pub-all-edit-${p.id}`} title="Editar publicación">
                  Editar
                </Button>
              )}
              {p.status !== "removed" && (
                <Button size="sm" variant="ghost" onClick={() => remove(p.id)} className="text-[#EF4444]" data-testid={`pub-all-remove-${p.id}`} title="Dar de baja"><Trash2 size={14} /></Button>
              )}
            </div>
          </div>
          );
        })}
      </div>
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))]" onClick={() => setEditing(null)}>
          <div className="bg-[#111] border border-[#262626] rounded-md p-6 max-w-md w-full max-h-[calc(100dvh-4rem)] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-xl mb-4">Editar publicación</h3>
            <div className="text-xs text-neutral-500 mb-3">{cardName(editing.card, editing.language)}</div>
            {isSealed(editing) && (
              <div className="mb-3 text-xs text-purple-300 bg-purple-500/10 border border-purple-500/30 rounded p-2">
                📦 Producto sellado — no lleva variante ni condición.
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-neutral-400">Idioma</Label>
                <select data-testid="pub-all-edit-language" value={editing.language || "en"} onChange={(e) => setEditing(p => ({ ...p, language: e.target.value }))} className="w-full bg-[#171717] border border-[#262626] rounded px-2 py-2 text-sm">
                  <option value="es">Español</option><option value="en">Inglés</option><option value="ja">Japonés</option><option value="otro">Otro</option>
                </select>
              </div>
              {!isSealed(editing) && (
                <>
                  <div>
                    <Label className="text-xs text-neutral-400">Variante</Label>
                    <select data-testid="pub-all-edit-variant" value={editing.variant || "normal"} onChange={(e) => setEditing(p => ({ ...p, variant: e.target.value }))} className="w-full bg-[#171717] border border-[#262626] rounded px-2 py-2 text-sm">
                      {/* Variantes según juego de la carta (feb 2026 fix
                          "editar Riftbound muestra Pokémon"). Grandfather:
                          si la publicación tiene una variante legacy fuera
                          de la whitelist actual, se preserva. */}
                      {variantsForCard(editing.card, editing.variant).map(v => (
                        <option key={v.value} value={v.value}>{v.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs text-neutral-400">Condición</Label>
                    <Select value={editing.condition || "NM"} onValueChange={(v) => setEditing(p => ({ ...p, condition: v }))}>
                      <SelectTrigger data-testid="pub-all-edit-condition" className="bg-[#171717] border-[#262626]"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#171717] border-[#262626] text-white">
                        {conditionOptions(isLotProduct(editing.card || editing) || editing.condition === "MIXED").map(o => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              <div>
                <Label className="text-xs text-neutral-400">Precio (neto)</Label>
                <Input data-testid="pub-all-edit-price" type="number" step="0.01" value={editing.price} onChange={(e) => setEditing(p => ({ ...p, price: e.target.value }))} className="bg-[#171717] border-[#262626]" />
              </div>
              <div>
                <Label className="text-xs text-neutral-400">Cantidad</Label>
                <Input data-testid="pub-all-edit-quantity" type="number" value={editing.quantity} onChange={(e) => setEditing(p => ({ ...p, quantity: e.target.value }))} className="bg-[#171717] border-[#262626]" />
              </div>
              <div>
                <Label className="text-xs text-neutral-400">Costo envío</Label>
                <Input data-testid="pub-all-edit-ship" type="number" step="0.01" value={editing.ship_cost} onChange={(e) => setEditing(p => ({ ...p, ship_cost: e.target.value }))} className="bg-[#171717] border-[#262626]" />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={save} className="bg-[#EF4444] hover:bg-[#DC2626]" data-testid="pub-all-edit-save">Guardar</Button>
              <Button variant="ghost" onClick={() => setEditing(null)}>Cancelar</Button>
            </div>
          </div>
        </div>
      )}
    </Section>
  );
}

function GhostCleaner({ onDone }) {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const { confirm } = useDialog();
  const load = () => api.get("/admin/cards/ghosts").then((r) => setInfo(r.data)).catch(() => setInfo(null));
  useEffect(() => { load(); }, []);
  if (!info) return null;
  const hide = async () => {
    const ok = await confirm({ title: `¿Ocultar ${info.count} cartas fantasma?`, description: "Solo plantillas sin foto y sin ningún uso (sin publicaciones, pedidos, subastas, precios ni wishlist). No se borran: se pueden restaurar y queda en auditoría.", confirmText: "Sí, ocultar" });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/cards/ghosts/hide", { expected_count: info.count });
      toast.success(`${r.data.hidden} cartas fantasma ocultas`);
      load(); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo ocultar"); load(); } finally { setBusy(false); }
  };
  return (
    <div className="p-3 mb-4 bg-amber-500/10 border border-amber-500/40 rounded text-xs text-amber-100 flex flex-wrap items-center gap-3" data-testid="ghost-cleaner">
      <div className="flex-1 min-w-[220px]">
        <b data-testid="ghost-count">{info.count}</b> cartas fantasma (sin foto y sin ningún uso).{info.count ? ` Ej.: ${(info.sample || []).slice(0, 4).map((c) => c.name_es || c.id).join(", ")}` : " No hay nada para ocultar."}
      </div>
      <Button size="sm" onClick={hide} disabled={busy || !info.count} className="bg-amber-600 hover:bg-amber-700" data-testid="ghost-hide-btn">Ocultar {info.count} fantasmas</Button>
    </div>
  );
}

function CardsNoImage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [includeCompleted, setIncludeCompleted] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const { confirm } = useDialog();
  const { open: openLightbox } = useLightbox();

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/cards/no-image", {
        params: { q: q || undefined, include_completed: includeCompleted, limit: 100 },
      });
      setList(r.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [includeCompleted]);

  const uploadRef = async (cardId, file) => {
    if (!file) return;
    setUploadingId(cardId);
    try {
      const path = await uploadPublic(file);
      await api.post(`/admin/cards/${cardId}/reference-image`, { image_path: path });
      toast.success("Imagen de referencia guardada. Ya se ve en el catálogo.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo subir");
    } finally { setUploadingId(null); }
  };

  const removeRef = async (cardId) => {
    const ok = await confirm({ title: "Quitar imagen de referencia", description: "El catálogo volverá a mostrar la carta sin imagen (o la foto del vendedor como fallback). ¿Confirmas?" });
    if (!ok) return;
    try {
      await api.delete(`/admin/cards/${cardId}/reference-image`);
      toast.success("Imagen quitada");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo quitar");
    }
  };

  const resolveUrl = (u) => u ? (u.startsWith("http") ? u : u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : `${process.env.REACT_APP_BACKEND_URL}/api/files/public/${u}`) : null;

  return (
    <Section
      title={`Cartas sin imagen del API (${list.length})`}
      actions={
        <label className="flex items-center gap-2 text-xs text-neutral-400 cursor-pointer">
          <input
            type="checkbox"
            data-testid="cards-noimg-include-completed"
            checked={includeCompleted}
            onChange={(e) => setIncludeCompleted(e.target.checked)}
            className="accent-[#EF4444]"
          />
          Incluir cartas ya completadas
        </label>
      }
    >
      <div className="p-3 mb-4 bg-blue-500/10 border border-blue-500/30 rounded text-xs text-blue-100 leading-relaxed">
        Estas son cartas del catálogo automático que <b>no traen foto</b>. Sube una imagen limpia (fondo neutro, sin filtros) y quedará como imagen oficial en el catálogo, la miniatura y el detalle. Prioridad: <b>imagen del catálogo</b> → <b>tu imagen de referencia</b> → foto del vendedor.
      </div>
      <GhostCleaner onDone={load} />
      <form
        onSubmit={(e) => { e.preventDefault(); load(); }}
        className="mb-4 flex gap-2"
        data-testid="cards-noimg-search-form"
      >
        <Input
          data-testid="cards-noimg-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por nombre, número o set…"
          className="bg-[#171717] border-[#262626] text-white"
        />
        <Button type="submit" className="bg-[#EF4444] hover:bg-[#DC2626]" data-testid="cards-noimg-search-btn">Buscar</Button>
      </form>
      {loading ? (
        <div className="text-neutral-500">Cargando…</div>
      ) : list.length === 0 ? (
        <div className="text-neutral-500 border border-dashed border-[#262626] rounded p-8 text-center" data-testid="cards-noimg-empty">
          {includeCompleted ? "No hay cartas sin imagen ni con referencia." : "¡Todo al día! Ninguna carta activa está sin imagen."}
        </div>
      ) : (
        <div className="space-y-2">
          {list.map((c) => {
            const hasAdminRef = !!c.admin_image_url;
            return (
              <div
                key={c.id}
                data-testid={`card-noimg-${c.id}`}
                className={`bg-[#111] border rounded-md p-3 flex gap-3 ${hasAdminRef ? "border-emerald-500/40" : "border-[#262626]"}`}
              >
                <button
                  type="button"
                  onClick={() => hasAdminRef && openLightbox([{ url: resolveUrl(c.admin_image_url) }])}
                  className="w-20 h-28 bg-[#0A0A0A] rounded overflow-hidden border border-[#262626] shrink-0 flex items-center justify-center"
                >
                  {hasAdminRef ? (
                    <img src={resolveUrl(c.admin_image_url)} alt="" className="w-full h-full object-contain" />
                  ) : (
                    <span className="text-[10px] text-neutral-600 text-center px-1">Sin imagen</span>
                  )}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{c.name_es || c.name_en || c.id}</div>
                  <div className="text-xs text-neutral-500 truncate">
                    {c.set_name || "—"} · #{c.number || "—"} · <span className="uppercase">{c.id?.split("-")[0] || ""}</span>
                  </div>
                  <div className="text-[11px] text-neutral-500 mt-1">
                    {c.active_publications > 0 ? (
                      <span className="text-amber-400 font-medium">{c.active_publications} publicación{c.active_publications === 1 ? "" : "es"} activa{c.active_publications === 1 ? "" : "s"}</span>
                    ) : (
                      "Sin publicaciones activas"
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <label className={`inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded cursor-pointer transition ${uploadingId === c.id ? "bg-neutral-700 text-neutral-400" : "bg-[#EF4444] hover:bg-[#DC2626] text-white"}`}>
                      <Upload size={12} />
                      <BusyLabel busy={uploadingId === c.id} text="Subiendo…">{hasAdminRef ? "Reemplazar imagen" : "Subir imagen de referencia"}</BusyLabel>
                      <input
                        data-testid={`card-noimg-upload-${c.id}`}
                        type="file"
                        accept="image/*,.heic,.heif"
                        disabled={uploadingId === c.id}
                        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) uploadRef(c.id, f); }}
                        className="hidden"
                      />
                    </label>
                    {hasAdminRef && (
                      <Button
                        size="sm"
                        variant="ghost"
                        data-testid={`card-noimg-remove-${c.id}`}
                        onClick={() => removeRef(c.id)}
                        className="text-[#EF4444] hover:bg-[#EF4444]/10 h-auto py-1.5"
                      >
                        <X size={12} className="mr-1" /> Quitar
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Section>
  );
}

function CustomCards() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [uploadingRef, setUploadingRef] = useState(false);
  const [status, setStatus] = useState("pending");
  const [q, setQ] = useState("");
  const [game, setGame] = useState("all"); // Fase 3 admin
  const { prompt } = useDialog();
  const { open } = useLightbox();
  const load = () => api.get("/admin/custom-cards", {
    params: { status, q, ...(game !== "all" ? { game } : {}) },
  }).then(r => setList(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, game]);
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 250);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [q]);
  const approve = async (id) => {
    try {
      const { data } = await api.post(`/admin/custom-cards/${id}/approve`);
      toast.success(data?.publication_id ? "Aprobada y publicada" : data?.publish_pending ? "Aprobada · publicación pendiente" : "Aprobada");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo aprobar");
    }
  };
  const reject = async (id) => {
    const reason = await prompt({ title: "Rechazar carta personalizada", label: "Motivo", required: true });
    if (!reason) return;
    await api.post(`/admin/custom-cards/${id}/reject`, { reason }); toast.success("Rechazada"); load();
  };
  const hardDelete = async (row) => {
    if (user?.role !== "admin") { toast.error("Solo super admin"); return; }
    const typed = await prompt({
      title: `⚠️ ELIMINAR "${row.name_es || row.id}" DEFINITIVAMENTE`,
      label: `Esta acción es IRREVERSIBLE. Borra la carta, todas sus publicaciones, historial de precios y wishlists. Escribe DELETE para confirmar.`,
      required: true,
    });
    if (typed !== "DELETE") { toast.error("Confirmación no coincide"); return; }
    try {
      const r = await api.delete(`/admin/cards/${row.id}/hard-delete`, { params: { confirm: "DELETE" } });
      const d = r.data.deleted || {};
      toast.success(`Eliminada: ${d.publications || 0} publicación(es), ${d.price_snapshots || 0} snapshot(s), ${d.wishlist_entries || 0} wishlist(s)`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo eliminar");
    }
  };
  // Mover una carta custom entre juegos en un click. Útil cuando el vendedor
  // (o el bug pre-fix del payload) la registró en el juego equivocado.
  const moveGame = async (row) => {
    const currentSlug = row.game_slug || "pokemon";
    const nextSlug = currentSlug === "riftbound" ? "pokemon" : "riftbound";
    const nextLabel = nextSlug === "riftbound" ? "Riftbound" : "Pokémon";
    const currentLabel = currentSlug === "riftbound" ? "Riftbound" : "Pokémon";
    const typed = await prompt({
      title: `Mover "${row.name_es || row.id}" a ${nextLabel}`,
      label: `La carta está actualmente en ${currentLabel}. Se moverá al catálogo de ${nextLabel} y sus publicaciones seguirán a la carta. Escribe MOVER para confirmar.`,
      required: true,
    });
    if (typed !== "MOVER") { toast.error("Confirmación no coincide"); return; }
    try {
      await api.put(`/admin/custom-cards/${row.id}`, { game_slug: nextSlug });
      toast.success(`Movida a ${nextLabel}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo mover");
    }
  };
  const markSealed = async (row) => {
    const options = [
      ["sealed_other", "Otro (promo, blister, etc.)"],
      ["sealed_booster", "Sobre (Booster Pack)"],
      ["sealed_booster_bundle", "Booster Bundle"],
      ["sealed_box", "Booster Box / Caja"],
      ["sealed_etb", "Elite Trainer Box (ETB)"],
      ["sealed_deck", "Starter Deck / Mazo"],
      ["sealed_collection", "Colección / Tin"],
      ["sealed_blister", "Blíster"],
      ["sealed_bundle", "Bundle / Lote"],
    ];
    const listStr = options.map(([v, l], i) => `${i + 1}. ${l} (${v})`).join("\n");
    const pick = await prompt({
      title: `Marcar "${row.name_es || row.id}" como sellado`,
      label: `Elige el tipo (1-${options.length}):\n${listStr}`,
      required: true,
    });
    const idx = parseInt(pick, 10) - 1;
    if (isNaN(idx) || idx < 0 || idx >= options.length) { toast.error("Opción inválida"); return; }
    try {
      const r = await api.post(`/admin/cards/${row.id}/mark-sealed`, { product_type: options[idx][0] });
      toast.success(`Convertida a sellado. ${r.data.publications_updated} publicación(es) actualizadas.`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo convertir");
    }
  };
  const saveEdit = async () => {
    if (!editing) return;
    try {
      await api.put(`/admin/custom-cards/${editing.id}`, {
        name_es: editing.name_es, set_name: editing.set_name, serie_name: editing.serie_name, rarity: editing.rarity || null,
        number: editing.number, language: editing.language, custom_notes: editing.custom_notes,
      });
      toast.success("Datos guardados"); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo guardar");
    }
  };
  const uploadRef = async (file) => {
    if (!editing || !file) return;
    setUploadingRef(true);
    try {
      const path = await uploadPublic(file);
      const r = await api.post(`/admin/custom-cards/${editing.id}/set-image`, { photo_path: path });
      toast.success("Foto de referencia subida");
      setEditing(c => ({ ...c, image_url: r.data.image_url }));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo subir");
    } finally {
      setUploadingRef(false);
    }
  };
  const resolveUrl = (u) => u ? (u.startsWith("http") ? u : u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : `${process.env.REACT_APP_BACKEND_URL}/api/files/public/${u}`) : null;
  const tabs = [
    { key: "pending", label: "Pendientes" },
    { key: "needs_info", label: "Falta dato" },
    { key: "publish_pending", label: "Publicación pendiente" },
    { key: "approved", label: "Aprobadas" },
    { key: "rejected", label: "Rechazadas" },
    { key: "all", label: "Todas" },
  ];
  const statusBadge = (s) => {
    const map = {
      pending: { text: "Pendiente", cls: "bg-amber-500/20 text-amber-300 border-amber-500/40" },
      needs_info: { text: "Falta dato", cls: "bg-[#FF6B5E]/20 text-[#FF6B5E] border-[#FF6B5E]/40" },
      approved: { text: "Aprobada", cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" },
      rejected: { text: "Rechazada", cls: "bg-red-500/20 text-red-300 border-red-500/40" },
    };
    const m = map[s] || { text: s || "—", cls: "bg-neutral-700 text-neutral-300 border-neutral-600" };
    return <span className={`text-[10px] px-2 py-0.5 rounded border ${m.cls} uppercase tracking-wider`}>{m.text}</span>;
  };
  return (
    <Section title={`Cartas personalizadas (${list.length})`}>
      <GameTabs value={game} onChange={setGame} testidPrefix="admin-customs" className="mb-3" />
      <div className="flex flex-wrap gap-2 mb-3">
        {tabs.map(t => (
          <button
            key={t.key}
            type="button"
            onClick={() => setStatus(t.key)}
            data-testid={`custom-tab-${t.key}`}
            className={`px-3 py-1.5 rounded text-xs uppercase tracking-wider border transition ${status === t.key ? "bg-[#EF4444] text-white border-[#EF4444]" : "bg-[#111] text-neutral-400 border-[#262626] hover:border-[#EF4444] hover:text-white"}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="mb-3">
        <Input
          data-testid="custom-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por nombre, set o ID…"
          className="bg-[#171717] border-[#262626] max-w-md"
        />
      </div>
      {list.length === 0 && <div className="text-neutral-500">Sin resultados.</div>}
      <div className="space-y-3">
        {list.map(c => (
          <div key={c.id} className="bg-[#111] border border-[#262626] rounded-md p-4" data-testid={`custom-card-${c.id}`}>
            <div className="flex gap-3">
              <div className="w-20 h-28 bg-[#0A0A0A] rounded overflow-hidden border border-[#262626] shrink-0 flex items-center justify-center">
                {c.image_url ? (
                  <img src={resolveUrl(c.image_url)} alt="ref" className="w-full h-full object-contain" />
                ) : (
                  <span className="text-[10px] text-neutral-600 text-center px-1">Sin foto de referencia</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-medium truncate">{c.name_es}</div>
                  {statusBadge(c.custom_status)}
                  {c.is_sealed && <span className="text-[10px] px-2 py-0.5 rounded border bg-purple-500/20 text-purple-300 border-purple-500/40 uppercase tracking-wider">Sellado</span>}
                </div>
                <div className="text-xs text-neutral-500">Serie: {c.serie_name || "—"} · Set: {c.set_name} · Núm: {c.number || "—"}</div>
                {(c.rarity || c.brand || c.size) && <div className="text-xs text-neutral-400" data-testid={`custom-extra-${c.id}`}>{[c.rarity && `Rareza: ${c.rarity}`, c.brand && `Marca: ${c.brand}`, c.size && `Medida: ${c.size}`].filter(Boolean).join(" · ")}</div>}
                <div className="text-xs text-neutral-500">Idioma: {(c.language || "es").toUpperCase()} · Variante: {c.variant || "normal"}</div>
                {c.pub_counts && c.pub_counts.total > 0 && (
                  <div className="text-[10px] text-neutral-500 mt-1">
                    Publicaciones: <span className="text-emerald-400">{c.pub_counts.active} activas</span>
                    {c.pub_counts.pending > 0 && <> · <span className="text-amber-400">{c.pub_counts.pending} en revisión</span></>}
                    {c.pub_counts.rejected > 0 && <> · <span className="text-red-400">{c.pub_counts.rejected} rechazadas</span></>}
                  </div>
                )}
                {c.custom_notes && <div className="text-xs text-neutral-400 mt-2 p-2 bg-[#0A0A0A] rounded">{c.custom_notes}</div>}
                {c.reject_reason && c.custom_status === "rejected" && (
                  <div className="text-xs text-red-300 mt-2 p-2 bg-red-500/10 rounded border border-red-500/30">
                    Motivo de rechazo: {c.reject_reason}
                  </div>
                )}
                {/* Info del vendedor que envió la carta — para que el admin
                     pueda contactarlo directo (nombre + email + WhatsApp).
                     Antes solo se veían 8 chars del ID, inútil para contacto.
                     WhatsApp requiere E.164 sin `+` (ej. 593988282770); si el
                     vendedor guardó "0988282770" WhatsApp lo busca como
                     username y falla. `normalizeEcPhone` corrige eso. */}
                {c.seller ? (() => {
                  const normalizeEcPhone = (raw) => {
                    if (!raw) return null;
                    let d = String(raw).replace(/[^0-9]/g, "");
                    if (!d) return null;
                    if (d.startsWith("593")) return d;               // ya en E.164
                    if (d.startsWith("0")) return "593" + d.slice(1); // 09XXX → 5939XXX
                    if (d.length === 9) return "593" + d;             // 9XXX sin 0
                    return d;                                          // otro país: dejar como está
                  };
                  const waNumber = normalizeEcPhone(c.seller.phone);
                  return (
                  <div className="mt-2 p-2 bg-[#0A0A0A] border border-[#262626] rounded text-xs" data-testid={`custom-seller-${c.id}`}>
                    <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1">Enviada por</div>
                    <div className="text-white font-medium truncate">{c.seller.name}</div>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-neutral-400">
                      {c.seller.email && (
                        <a href={`mailto:${c.seller.email}`} className="hover:text-[#EF4444] break-all" data-testid={`custom-seller-email-${c.id}`}>
                          {c.seller.email}
                        </a>
                      )}
                      {c.seller.phone && waNumber && (
                        <a href={`https://wa.me/${waNumber}`} target="_blank" rel="noopener noreferrer" className="hover:text-emerald-400" data-testid={`custom-seller-whatsapp-${c.id}`}>
                          WhatsApp: {c.seller.phone}
                        </a>
                      )}
                      {c.seller.cedula && (
                        <span className="text-neutral-500">CI: {c.seller.cedula}</span>
                      )}
                    </div>
                    <div className="text-[10px] text-neutral-600 mt-1">
                      Enviada {new Date(c.created_at).toLocaleDateString("es-EC")} · ID {c.seller.id?.slice(0, 8)}
                    </div>
                  </div>
                  );
                })() : (
                  <div className="text-[10px] text-neutral-600 mt-1">Vendedor no encontrado · {new Date(c.created_at).toLocaleDateString("es-EC")}</div>
                )}
                {!c.image_url && c.custom_status === "pending" && (
                  <div className="mt-2 text-xs text-amber-400">
                    Falta subir la foto de referencia limpia antes de poder aprobar esta carta.
                  </div>
                )}
              </div>
            </div>
            {c.identification_photos?.length > 0 && (
              <div className="mt-3">
                <div className="text-[10px] text-neutral-500 uppercase tracking-wider mb-1">Fotos enviadas por el vendedor para identificar (click para ampliar)</div>
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                  {c.identification_photos.map((p, i) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => open(c.identification_photos.map(x => ({ path: x, scope: "public" })), i)}
                      data-testid={`custom-idphoto-${c.id}-${i}`}
                      className="focus:outline-none focus:ring-2 focus:ring-[#EF4444]"
                    >
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/files/public/${p}`}
                        alt=""
                        className="w-full aspect-[3/4] object-cover rounded border border-[#262626] hover:border-[#EF4444] cursor-zoom-in transition"
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}
            <PossibleDuplicate c={c} />
            <DraftPanel c={c} />
            <NeedsInfoNote c={c} />
            <div className="flex flex-wrap gap-2 mt-3">
              <RetryPublishButton c={c} onDone={load} />
              <Button size="sm" onClick={() => setEditing({ ...c })} className="bg-amber-600 hover:bg-amber-700" data-testid={`custom-edit-${c.id}`}>
                Editar y subir foto de referencia
              </Button>
              {c.custom_status === "pending" && (
                <Button size="sm" onClick={() => approve(c.id)} className="bg-emerald-600 hover:bg-emerald-700" disabled={!c.image_url} data-testid={`custom-approve-${c.id}`}>
                  {c.pub_draft ? "Aprobar y publicar" : "Aprobar"}
                </Button>
              )}
              {c.custom_status === "pending" && <RequestInfoButton c={c} onDone={load} />}
              {c.custom_status === "pending" && <DuplicatesButton c={c} onDone={load} />}
              {c.custom_status !== "rejected" && (
                <Button size="sm" variant="outline" onClick={() => reject(c.id)} className="border-[#EF4444] text-[#EF4444]" data-testid={`custom-reject-${c.id}`}>
                  {c.custom_status === "approved" ? "Retirar / Rechazar" : "Rechazar"}
                </Button>
              )}
              {c.custom_status === "rejected" && (
                <Button size="sm" onClick={() => approve(c.id)} className="bg-emerald-600 hover:bg-emerald-700" disabled={!c.image_url} data-testid={`custom-reapprove-${c.id}`}>
                  Re-aprobar
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => markSealed(c)} className="border-purple-500 text-purple-300 hover:bg-purple-500/10" data-testid={`custom-mark-sealed-${c.id}`}>
                Marcar como sellado
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => moveGame(c)}
                className={`${(c.game_slug || "pokemon") === "riftbound" ? "border-blue-500 text-blue-300 hover:bg-blue-500/10" : "border-orange-500 text-orange-300 hover:bg-orange-500/10"}`}
                data-testid={`custom-move-game-${c.id}`}
                title={`Actualmente en ${(c.game_slug || "pokemon") === "riftbound" ? "Riftbound" : "Pokémon"}`}
              >
                Mover a {(c.game_slug || "pokemon") === "riftbound" ? "Pokémon" : "Riftbound"}
              </Button>
              {user?.role === "admin" && (
                <Button size="sm" onClick={() => hardDelete(c)} className="bg-black border border-red-500 text-red-400 hover:bg-red-500 hover:text-white" data-testid={`custom-hard-delete-${c.id}`}>
                  Eliminar definitivamente
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))]" onClick={() => setEditing(null)}>
          <div className="bg-[#111] border border-[#262626] rounded-md p-6 max-w-md w-full max-h-[calc(100dvh-4rem)] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-xl mb-4">Editar carta personalizada</h3>
            <div className="space-y-3">
              <div><Label className="text-xs text-neutral-400">Nombre</Label><Input data-testid="custom-edit-name" value={editing.name_es || ""} onChange={(e) => setEditing(c => ({ ...c, name_es: e.target.value }))} className="bg-[#171717] border-[#262626]" /></div>
              <div><Label className="text-xs text-neutral-400">Set / Edición</Label><Input data-testid="custom-edit-set" value={editing.set_name || ""} onChange={(e) => setEditing(c => ({ ...c, set_name: e.target.value }))} className="bg-[#171717] border-[#262626]" /></div>
              <div><Label className="text-xs text-neutral-400">Rareza</Label><Input data-testid="custom-edit-rarity" value={editing.rarity || ""} onChange={(e) => setEditing((c) => ({ ...c, rarity: e.target.value }))} /></div>

              <div><Label className="text-xs text-neutral-400">Serie</Label><Input data-testid="custom-edit-serie" value={editing.serie_name || ""} onChange={(e) => setEditing(c => ({ ...c, serie_name: e.target.value }))} className="bg-[#171717] border-[#262626]" /></div>
              <div><Label className="text-xs text-neutral-400">Número de colección</Label><Input value={editing.number || ""} onChange={(e) => setEditing(c => ({ ...c, number: e.target.value }))} className="bg-[#171717] border-[#262626]" /></div>
              <div>
                <Label className="text-xs text-neutral-400">Idioma</Label>
                <select
                  data-testid="custom-edit-language"
                  value={editing.language || "es"}
                  onChange={(e) => setEditing(c => ({ ...c, language: e.target.value }))}
                  className="w-full h-9 bg-[#171717] border border-[#262626] rounded-md px-2 text-sm text-white focus:outline-none focus:border-[#EF4444]"
                >
                  <option value="es">Español (ES)</option>
                  <option value="en">Inglés (EN)</option>
                  <option value="ja">Japonés (JA)</option>
                  <option value="na">N/A (sellado)</option>
                </select>
              </div>
              <div><Label className="text-xs text-neutral-400">Notas</Label><Input value={editing.custom_notes || ""} onChange={(e) => setEditing(c => ({ ...c, custom_notes: e.target.value }))} className="bg-[#171717] border-[#262626]" /></div>

              <div className="border-t border-[#262626] pt-3">
                <Label className="text-xs text-neutral-400 block mb-2">Foto de referencia (queda ligada a la CARTA)</Label>
                {editing.image_url ? (
                  <div className="flex items-start gap-3 mb-2">
                    <img src={resolveUrl(editing.image_url)} alt="ref actual" className="w-24 h-32 object-contain bg-[#0A0A0A] rounded border border-[#262626]" data-testid="custom-edit-ref-preview" />
                    <div className="text-xs text-neutral-500 flex-1">Puedes reemplazarla subiendo otra imagen.</div>
                  </div>
                ) : (
                  <div className="text-xs text-amber-300 mb-2">Aún no hay foto de referencia. Sube una imagen limpia.</div>
                )}
                <label className={`inline-flex items-center gap-2 px-3 py-2 rounded border cursor-pointer ${uploadingRef ? "border-neutral-700 text-neutral-500" : "border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10"}`} data-testid="custom-upload-ref">
                  <Upload size={14} />
                  <BusyLabel busy={uploadingRef} text="Subiendo…">Subir foto de referencia</BusyLabel>
                  <input type="file" accept="image/*" className="hidden" disabled={uploadingRef} onChange={(e) => { if (e.target.files?.[0]) uploadRef(e.target.files[0]); e.target.value = ""; }} />
                </label>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={saveEdit} className="bg-[#EF4444] hover:bg-[#DC2626]" data-testid="custom-edit-save">Guardar datos</Button>
              <Button variant="ghost" onClick={() => setEditing(null)}>Cerrar</Button>
            </div>
          </div>
        </div>
      )}
    </Section>
  );
}

function RefundRequests() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/admin/refund-requests").then(r => setList(r.data)); }, []);
  return (
    <Section title={`Solicitudes de reembolso (${list.length})`}>
      {list.length === 0 && <div className="text-neutral-500">Sin solicitudes.</div>}
      <div className="space-y-3">
        {list.map(r => (
          <a key={r.id} href={`/order/${r.order_id}`} className="block bg-[#111] border border-[#262626] rounded-md p-4 hover:border-[#EF4444]">
            <div className="text-sm font-medium">Pedido #{r.order_id.slice(0, 8)} — ${r.order?.total?.toFixed(2)}</div>
            <div className="text-sm text-neutral-400 mt-1">{r.reason}</div>
            {r.details && <div className="text-xs text-neutral-500 mt-1">{r.details}</div>}
          </a>
        ))}
      </div>
    </Section>
  );
}

function ManualRefunds() {
  const { confirm, prompt } = useDialog();
  const flowOn = usePublicConfig().admin_flow_controls === true;
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(null);
  const [form, setForm] = useState({ transfer_receipt_path: "", transfer_date: "", notes: "" });
  const [showForm, setShowForm] = useState(null);

  const load = () => api.get("/admin/manual-refunds").then(r => setList(r.data));
  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy("upload");
    try {
      const p = await uploadPublic(f);
      setForm(fm => ({ ...fm, transfer_receipt_path: p }));
      toast.success("Comprobante subido");
    } catch { toast.error("No se pudo subir"); } finally { setBusy(null); }
  };

  const markDone = async (orderId) => {
    if (!form.transfer_receipt_path || !form.transfer_date) { toast.error("Comprobante y fecha son obligatorios"); return; }
    setBusy(orderId);
    try {
      await api.post(`/admin/orders/${orderId}/mark-manual-refund-done`, form);
      toast.success("Reembolso manual cerrado y notificado al comprador");
      setShowForm(null);
      setForm({ transfer_receipt_path: "", transfer_date: "", notes: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setBusy(null); }
  };

  const closeOutOfBand = async (r) => {
    const note = await prompt({ title: "Marcar reembolso manual hecho y cerrar", description: `Registra UN reembolso de $${r.total.toFixed(2)} al comprador como YA transferido por fuera. El pedido queda REEMBOLSADO y no se puede deshacer. La carta NO vuelve al catálogo.`, label: "Nota obligatoria (ej. reembolsado por transferencia el 12/10)", multiline: true, required: true });
    if (!note) return;
    if (!(await confirm({ title: "¿Cerrar el reembolso?", description: `Reembolso de $${r.total.toFixed(2)} ya transferido. No se puede deshacer.`, confirmText: "Sí, cerrar", destructive: true }))) return;
    setBusy(`oob-${r.id}`);
    try {
      await api.post(`/admin/orders/${r.id}/manual-refund-close`, { note: note.trim() });
      toast.success("Reembolso manual cerrado");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo cerrar el reembolso");
    } finally { setBusy(null); }
  };

  return (
    <Section title={`Devoluciones manuales pendientes (${list.length})`}>
      {list.length === 0 && <div className="text-neutral-500 text-sm">Sin devoluciones manuales pendientes.</div>}
      <div className="space-y-3">
        {list.map(r => (
          <div key={r.id} data-testid={`manual-refund-${r.id}`} className="bg-[#111] border border-amber-500/40 rounded-md p-4">
            <div className="flex justify-between items-start gap-4 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="text-sm">Pedido <a href={`/order/${r.id}`} className="text-[#EF4444] font-mono hover:underline">#{r.id.slice(0,8)}</a> · <b className="text-white">${r.total.toFixed(2)}</b></div>
                <div className="text-xs text-neutral-400 mt-1">Comprador: <b className="text-white">{r.buyer?.name}</b> · {r.buyer?.email}</div>
                <div className="text-xs text-neutral-400">Vendedor: <b className="text-white">{r.seller?.name}</b> · {r.seller?.email}</div>
                <div className="text-xs text-neutral-500 mt-1">Motivo: {r.refund_reason}</div>
                <div className="text-[10px] text-amber-300 mt-1">
                  {r.refund_status === "awaiting_bank_info" ? "⏳ Esperando datos bancarios del comprador" :
                   r.refund_status === "awaiting_transfer" ? "💸 Datos recibidos — pendiente transferir" : r.refund_status}
                  {r.refund_manual_ineligibility && ` · ${r.refund_manual_ineligibility}`}
                </div>
              </div>
              {r.refund_status === "awaiting_transfer" && r.refund_bank_info && (
                <div className="bg-black/40 border border-[#262626] rounded p-3 text-xs text-neutral-300 min-w-[260px]">
                  <div>Banco: <b className="text-white">{r.refund_bank_info.bank}</b> · {r.refund_bank_info.account_type}</div>
                  <div>Cuenta: <b className="text-white">{r.refund_bank_info.account_number}</b></div>
                  <div>Titular: <b className="text-white">{r.refund_bank_info.holder_name}</b></div>
                  <div>Cédula: <b className="text-white">{r.refund_bank_info.holder_cedula}</b></div>
                </div>
              )}
            </div>
            {r.refund_status === "awaiting_transfer" && showForm !== r.id && (
              <Button size="sm" className="mt-3 bg-emerald-600 hover:bg-emerald-700" data-testid={`mark-done-${r.id}`} onClick={() => setShowForm(r.id)}>
                Marcar transferido
              </Button>
            )}
            {flowOn && showForm !== r.id && (
              <Button size="sm" variant="outline" className="mt-3 ml-2 border-emerald-600 text-emerald-400" data-testid={`oob-close-${r.id}`} disabled={!!busy} onClick={() => closeOutOfBand(r)}>
                <BusyLabel busy={busy === `oob-${r.id}`} text="Cerrando…">Marcar reembolso manual hecho y cerrar</BusyLabel>
              </Button>
            )}
            {showForm === r.id && (
              <div className="mt-3 space-y-2 border-t border-[#262626] pt-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-neutral-400 uppercase">Fecha de transferencia</Label>
                    <Input type="date" data-testid={`transfer-date-${r.id}`} value={form.transfer_date} onChange={(e) => setForm({ ...form, transfer_date: e.target.value })} className="bg-[#171717] border-[#262626]" />
                  </div>
                  <div>
                    <Label className="text-xs text-neutral-400 uppercase">Comprobante</Label>
                    <Input type="file" accept="image/*,application/pdf" onChange={onFile} data-testid={`transfer-file-${r.id}`} className="bg-[#171717] border-[#262626]" />
                    {form.transfer_receipt_path && <div className="text-[10px] text-emerald-400 mt-1">✓ subido</div>}
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-neutral-400 uppercase">Notas (opcional)</Label>
                  <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-[#171717] border-[#262626]" />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid={`confirm-done-${r.id}`} disabled={busy === r.id} onClick={() => markDone(r.id)}>
                    <BusyLabel busy={busy === r.id} text="Guardando…">Confirmar y cerrar reembolso</BusyLabel>
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setShowForm(null); setForm({ transfer_receipt_path: "", transfer_date: "", notes: "" }); }}>Cancelar</Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </Section>
  );
}

function SellerDebts() {
  const [list, setList] = useState([]);
  const [status, setStatus] = useState("pending");
  const { confirm } = useDialog();

  const load = () => api.get("/admin/seller-debts", { params: { status } }).then(r => setList(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const forgive = async (id) => {
    const ok = await confirm({ title: "Condonar deuda", description: "Marcarás esta deuda como perdonada. No se cobrará al vendedor.", confirmLabel: "Sí, condonar", danger: true });
    if (!ok) return;
    try {
      await api.post(`/admin/seller-debts/${id}/forgive`);
      toast.success("Deuda condonada");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const total = list.filter(d => d.status === "pending").reduce((s, d) => s + Number(d.amount), 0);

  return (
    <Section title={`Deudas de vendedores (${list.length}) · pendiente $${total.toFixed(2)}`} actions={
      <select data-testid="debts-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="bg-[#171717] border border-[#262626] rounded px-3 py-1 text-sm">
        <option value="pending">Pendientes</option>
        <option value="settled">Cobradas</option>
        <option value="forgiven">Condonadas</option>
        <option value="all">Todas</option>
      </select>
    }>
      {list.length === 0 && <div className="text-neutral-500 text-sm">Sin deudas.</div>}
      <div className="bg-[#111] border border-[#262626] rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-neutral-500 text-xs uppercase border-b border-[#262626]">
            <tr><th className="text-left p-3">Vendedor</th><th className="text-left p-3">Motivo</th><th className="text-left p-3">Pedido</th><th className="text-right p-3">Monto</th><th className="text-left p-3">Estado</th><th></th></tr>
          </thead>
          <tbody>
            {list.map(d => (
              <tr key={d.id} className="border-b border-[#171717]" data-testid={`debt-row-${d.id}`}>
                <td className="p-3">
                  <div className="font-medium">{d.seller?.name}</div>
                  <div className="text-xs text-neutral-500">{d.seller?.email}</div>
                  {d.seller?.strikes > 0 && <span className="text-[10px] text-amber-400">{d.seller.strikes} strike(s)</span>}
                  {d.seller?.banned && <span className="text-[10px] text-red-400 ml-1">BANEADO</span>}
                </td>
                <td className="p-3 text-neutral-300">{d.reason}</td>
                <td className="p-3 font-mono text-xs"><a href={`/order/${d.order_id}`} className="text-[#EF4444] hover:underline">#{d.order_id.slice(0,8)}</a></td>
                <td className="p-3 text-right text-[#EF4444] font-medium">${Number(d.amount).toFixed(2)}</td>
                <td className="p-3"><span className={`text-[11px] uppercase ${d.status === "pending" ? "text-amber-400" : d.status === "settled" ? "text-emerald-400" : "text-neutral-500"}`}>{d.status}</span></td>
                <td className="p-3 text-right">
                  {d.status === "pending" && <Button size="sm" variant="ghost" className="text-[#EF4444]" data-testid={`forgive-${d.id}`} onClick={() => forgive(d.id)}>Condonar</Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}


// ============================================================
// Feb 2026 v27 — Panel "Pedidos duplicados" (super_admin only)
// ------------------------------------------------------------
// UI sobre los endpoints ya existentes:
//   GET  /admin/orders/duplicates-by-receipt?receipt_number=X
//   POST /admin/orders/{order_id}/cancel-duplicate
// NUNCA mueve dinero — la regla dura vive en el backend. Este panel
// solo dispara los endpoints y muestra los flags visualmente para que
// el admin nunca pueda ni intentar cancelar el pedido con pago real.
// ============================================================
function Duplicates() {
  const [receiptNumber, setReceiptNumber] = useState("");
  const [orders, setOrders] = useState([]);
  const [count, setCount] = useState(null);
  const [busy, setBusy] = useState(false);
  const [busyCancel, setBusyCancel] = useState(null);
  const [keeperId, setKeeperId] = useState("");
  const [searched, setSearched] = useState(false);
  const { confirm } = useDialog();

  const search = async () => {
    const rn = (receiptNumber || "").trim();
    if (!rn) { toast.error("Escribe el número de comprobante"); return; }
    setBusy(true);
    setSearched(true);
    setKeeperId("");
    try {
      const r = await api.get(`/admin/orders/duplicates-by-receipt?receipt_number=${encodeURIComponent(rn)}`);
      setOrders(r.data.orders || []);
      setCount(r.data.count ?? 0);
      // Auto-elegir keeper: la única con "has_real_money" si es única
      const withMoney = (r.data.orders || []).filter(o => o.flag === "has_real_money");
      if (withMoney.length === 1) setKeeperId(withMoney[0].id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error buscando");
      setOrders([]); setCount(0);
    } finally {
      setBusy(false);
    }
  };

  const cancelOne = async (order) => {
    if (!keeperId) { toast.error("Primero marca cuál es el KEEPER"); return; }
    if (keeperId === order.id) { toast.error("No puedes cancelar el KEEPER — es el que se conserva"); return; }
    const ok = await confirm({
      title: "¿Cancelar como duplicado?",
      description: `Esto cancela el pedido #${order.id.slice(0,8)} marcándolo como duplicado del pedido #${keeperId.slice(0,8)}. NO mueve dinero — solo cierra el pedido.`,
      destructive: true,
      confirmText: "Cancelar duplicado",
    });
    if (!ok) return;
    setBusyCancel(order.id);
    try {
      const r = await api.post(`/admin/orders/${order.id}/cancel-duplicate`, {
        reason: "duplicado - mismo comprobante",
        keeper_order_id: keeperId,
      });
      toast.success(`Pedido #${order.id.slice(0,8)} cancelado como duplicado del #${keeperId.slice(0,8)}. Settlement: ${r.data.settlement}.`);
      await search(); // refresca la tabla
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || detail || "Error cancelando");
      toast.error(msg, { duration: 8000 });
    } finally {
      setBusyCancel(null);
    }
  };

  return (
    <Section title="Pedidos duplicados por comprobante">
      <div className="bg-[#0A0A0A] border border-amber-500/30 rounded-md p-4 mb-4">
        <div className="text-sm text-amber-200 font-medium mb-1">¿Cómo funciona?</div>
        <div className="text-xs text-neutral-400 leading-relaxed">
          1. Pega el número de comprobante bancario del caso. 2. Marca cuál pedido es el <b className="text-white">KEEPER</b> (el que se conserva — normalmente el que ya aceptaste porque tiene el pago real). 3. Cancela los otros con el botón rojo.
          <br/><span className="text-amber-300">Regla dura del backend</span>: pedidos con pago propio (movimientos de dinero) tienen el botón deshabilitado — no se pueden cancelar por acá; para esos usa el flujo de reembolso normal.
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <Input
          data-testid="duplicates-search-input"
          placeholder="Número de comprobante"
          value={receiptNumber}
          onChange={(e) => setReceiptNumber(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") search(); }}
          className="bg-[#171717] border-[#262626] max-w-xs"
        />
        <Button
          data-testid="duplicates-search-btn"
          onClick={search}
          disabled={busy}
          className="bg-[#EF4444] hover:bg-[#DC2626]"
        >
          {busy ? "Buscando…" : "Buscar"}
        </Button>
      </div>

      {searched && count === 0 && (
        <div className="text-neutral-500 text-sm bg-[#0A0A0A] border border-[#262626] rounded p-4">
          Sin pedidos con ese comprobante.
        </div>
      )}

      {orders.length > 0 && (
        <>
          <div className="text-xs text-neutral-400 mb-2">
            {count} pedido{count === 1 ? "" : "s"} comparten el comprobante <b className="text-white font-mono">{receiptNumber}</b>.
            {!keeperId && <span className="text-amber-300"> · Marca el KEEPER para habilitar los botones de cancelar.</span>}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse" data-testid="duplicates-table">
              <thead>
                <tr className="text-left text-xs uppercase text-neutral-500 border-b border-[#262626]">
                  <th className="py-2 px-2">Keeper</th>
                  <th className="py-2 px-2">Pedido</th>
                  <th className="py-2 px-2">Comprador</th>
                  <th className="py-2 px-2">Estado</th>
                  <th className="py-2 px-2">Monto</th>
                  <th className="py-2 px-2">Pago real</th>
                  <th className="py-2 px-2">Acción</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => {
                  const isCancelled = o.status === "CANCELADO";
                  const hasMoney = o.flag === "has_real_money";
                  const isKeeper = keeperId === o.id;
                  const canCancel = !isCancelled && !hasMoney && !isKeeper && !!keeperId && (o.status === "PENDIENTE_PAGO" || o.status === "PAGO_POR_VERIFICAR");
                  return (
                    <tr key={o.id} data-testid={`duplicate-row-${o.id.slice(0,8)}`} className={`border-b border-[#1a1a1a] ${isCancelled ? "opacity-50" : ""} ${isKeeper ? "bg-emerald-950/30" : ""}`}>
                      <td className="py-3 px-2">
                        <input
                          type="radio"
                          name="keeper"
                          data-testid={`duplicate-keeper-${o.id.slice(0,8)}`}
                          checked={isKeeper}
                          onChange={() => setKeeperId(o.id)}
                          disabled={isCancelled}
                          className="cursor-pointer"
                        />
                      </td>
                      <td className="py-3 px-2">
                        <a href={`/order/${o.id}`} target="_blank" rel="noreferrer" className="text-[#EF4444] font-mono text-xs hover:underline">#{o.id.slice(0,8)}</a>
                        {o.duplicate_of && <div className="text-[10px] text-neutral-500">dup de #{o.duplicate_of.slice(0,8)}</div>}
                      </td>
                      <td className="py-3 px-2">
                        <div className="text-xs">{o.buyer_name || "?"}</div>
                        <div className="text-[10px] text-neutral-500 font-mono">{o.buyer_id?.slice(0,10)}…</div>
                      </td>
                      <td className="py-3 px-2">
                        <span className={`text-[10px] px-2 py-0.5 rounded font-medium ${
                          isCancelled ? "bg-neutral-800 text-neutral-400" :
                          o.status === "PAGO_POR_VERIFICAR" ? "bg-amber-900/60 text-amber-200" :
                          o.status === "PENDIENTE_PAGO" ? "bg-blue-900/60 text-blue-200" :
                          o.status === "PAGADO" ? "bg-emerald-900/60 text-emerald-200" :
                          "bg-neutral-800 text-neutral-300"
                        }`}>{o.status}</span>
                      </td>
                      <td className="py-3 px-2 font-mono text-xs">${(o.total || 0).toFixed(2)}</td>
                      <td className="py-3 px-2">
                        {hasMoney ? (
                          <span data-testid={`duplicate-flag-money-${o.id.slice(0,8)}`} className="text-[10px] px-2 py-0.5 rounded bg-red-900/60 text-red-200 font-bold border border-red-500/40">
                            TIENE PAGO REAL ({o.money_movements?.length || 0} mov)
                          </span>
                        ) : (
                          <span data-testid={`duplicate-flag-nomoney-${o.id.slice(0,8)}`} className="text-[10px] px-2 py-0.5 rounded bg-neutral-800 text-neutral-400">
                            sin_pago_propio
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-2">
                        {isCancelled ? (
                          <span className="text-[10px] text-neutral-500 italic">{o.cancel_reason || "cancelado"}</span>
                        ) : isKeeper ? (
                          <span className="text-[10px] text-emerald-300 font-medium">✓ KEEPER (se conserva)</span>
                        ) : hasMoney ? (
                          <div className="text-[10px] text-red-300">
                            No cancelable como duplicado.<br/>
                            <span className="text-neutral-400">Usa flujo de reembolso.</span>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            data-testid={`duplicate-cancel-${o.id.slice(0,8)}`}
                            onClick={() => cancelOne(o)}
                            disabled={!canCancel || busyCancel === o.id}
                            className={`text-xs h-7 ${canCancel ? "bg-[#EF4444] hover:bg-[#DC2626]" : "bg-neutral-800 text-neutral-500 cursor-not-allowed"}`}
                          >
                            {busyCancel === o.id ? "Cancelando…" : "Cancelar duplicado"}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Section>
  );
}


// ============================================================
// Feb 2026 v29 — Panel "Yu-Gi-Oh Seed" (super_admin only)
// ------------------------------------------------------------
// Monitorea + dispara el seed de ~13 000 cartas Yu-Gi-Oh y sus imágenes a
// Cloudflare R2. Auto-refresh cada 3s mientras un job está corriendo.
// El flag `yugioh_enabled` sigue en FALSE hasta Slice 3; este panel NO lo
// toca — es solo poblamiento del catálogo.
// ============================================================
function YugiohSeed() {
  const [data, setData] = useState(null);
  const [limit, setLimit] = useState(50);
  const [busy, setBusy] = useState(false);
  const { confirm } = useDialog();

  const load = async () => {
    try {
      const r = await api.get("/admin/yugioh/seed-all/status");
      setData(r.data);
    } catch (err) {
      // silent — polling se recupera solo
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!data?.any_job_running) return;
    const id = setInterval(load, 3000);
    return () => clearInterval(id);
  }, [data?.any_job_running]);

  const startSeed = async (lim) => {
    const label = lim ? `prueba chica (${lim} cartas)` : "SEED COMPLETO (~13 000 cartas)";
    const ok = await confirm({
      title: `¿Correr ${label}?`,
      description: lim
        ? `Esto baja ${lim} cartas al mirror y sube sus imágenes a R2. Toma 1-2 min. NO habilita Yu-Gi-Oh en el catálogo (flag sigue OFF).`
        : "Esto baja TODAS las ~13 000 cartas y sube sus imágenes a R2. Toma 20-30 min. NO habilita Yu-Gi-Oh en el catálogo. Solo correr tras validar la prueba chica.",
      destructive: !lim,
      confirmText: lim ? "Correr prueba" : "Correr seed completo",
    });
    if (!ok) return;
    setBusy(true);
    try {
      const params = lim ? `?limit=${lim}` : "";
      const r = await api.post(`/admin/yugioh/seed-all${params}`);
      toast.success(r.data.message || "Seed iniciado");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  const toggleYugiohFlag = async () => {
    const currentlyOn = !!data?.yugioh_enabled;
    const nextValue = !currentlyOn;
    const ok = await confirm({
      title: currentlyOn ? "¿Desactivar Yu-Gi-Oh! en producción?" : "¿Activar Yu-Gi-Oh! en producción?",
      description: currentlyOn
        ? "La pestaña Yu-Gi-Oh! desaparece del catálogo público al instante. Las publicaciones YGO existentes quedan ocultas (no se borran). Reversible con un click."
        : "La pestaña Yu-Gi-Oh! aparece en el catálogo público. Vendedores podrán publicar cartas Yu-Gi-Oh! con rareza+set. Asegúrate que el seed terminó (ok ≈ 14.595, failed = 0). Reversible con un click.",
      destructive: !nextValue,
      confirmText: nextValue ? "Activar Yu-Gi-Oh!" : "Desactivar Yu-Gi-Oh!",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.put("/admin/config", { key: "yugioh_enabled", value: nextValue });
      toast.success(nextValue ? "Yu-Gi-Oh! ACTIVADO — ya es visible al público" : "Yu-Gi-Oh! desactivado — invisible al público");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cambiar el flag");
    } finally {
      setBusy(false);
    }
  };

  const retryFailed = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/yugioh-images/backfill?limit=100");
      toast.success("Reintento disparado");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <Section title="Yu-Gi-Oh Seed"><div className="text-neutral-500 text-sm">Cargando…</div></Section>;

  const r2 = data.r2 || {};
  const mir = data.mirror || {};
  const img = data.images || {};
  const total = img.total || 0;
  const okCount = img.ok_count || 0;
  const pctOk = total ? Math.round((okCount / total) * 100) : 0;

  return (
    <Section title="Yu-Gi-Oh Seed (Slice 2)">
      {/* R2 Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="bg-[#0A0A0A] border border-[#262626] rounded-md p-4" data-testid="yugioh-r2-status">
          <div className="text-xs uppercase text-neutral-500 mb-2">Cloudflare R2</div>
          <div className="flex items-center gap-2 mb-2">
            <span className={`text-xs px-2 py-0.5 rounded font-medium ${r2.configured ? "bg-emerald-900/60 text-emerald-200" : "bg-red-900/60 text-red-200"}`}>
              {r2.configured ? "CONFIGURADO" : "NO CONFIGURADO"}
            </span>
            {r2.bucket && <span className="text-xs text-neutral-400 font-mono">bucket: {r2.bucket}</span>}
          </div>
          <div className="text-[11px] text-neutral-400 space-y-0.5">
            <div>account_id: {r2.account_id_set ? "✓" : "✗"}</div>
            <div>access_key: {r2.access_key_set ? "✓" : "✗"}</div>
            <div>secret: {r2.secret_set ? "✓" : "✗"}</div>
            {r2.bucket_reachable !== undefined && (
              <div>bucket alcanzable: {r2.bucket_reachable ? "✓" : `✗ (${r2.bucket_error || "error"})`}</div>
            )}
          </div>
          {!r2.configured && (
            <div className="mt-3 text-[11px] text-amber-300 border-l-2 border-amber-500 pl-2">
              Agrega los 4 secrets en Deployment → Secrets Tab antes de correr el seed.
            </div>
          )}
        </div>

        <div className="bg-[#0A0A0A] border border-[#262626] rounded-md p-4" data-testid="yugioh-mirror-status">
          <div className="text-xs uppercase text-neutral-500 mb-2">Mirror (db.yugioh_mirror)</div>
          <div className="text-2xl font-bold text-white mb-1">{mir.card_count || 0}</div>
          <div className="text-[11px] text-neutral-500">cartas en el espejo</div>
          {mir.refreshing && <div className="text-[11px] text-blue-300 mt-2 animate-pulse">⟳ refresh en curso…</div>}
          {mir.last_ok_at && <div className="text-[10px] text-neutral-500 mt-2">último ok: {new Date(mir.last_ok_at).toLocaleString("es-EC")}</div>}
          {mir.last_error && <div className="text-[10px] text-red-300 mt-1 font-mono">error: {mir.last_error.slice(0, 100)}</div>}
        </div>
      </div>

      {/* Images breakdown */}
      <div className="bg-[#0A0A0A] border border-[#262626] rounded-md p-4 mb-4" data-testid="yugioh-images-status">
        <div className="flex items-baseline justify-between mb-3">
          <div className="text-xs uppercase text-neutral-500">Imágenes en R2</div>
          <div className="text-xs text-neutral-400">{okCount} / {total} ({pctOk}%)</div>
        </div>
        <div className="w-full bg-[#171717] rounded-full h-2 mb-3 overflow-hidden">
          <div className="bg-emerald-500 h-2 transition-all duration-500" style={{ width: `${pctOk}%` }} />
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="bg-emerald-950/40 border border-emerald-500/30 rounded p-2">
            <div className="text-lg font-bold text-emerald-300" data-testid="yugioh-img-ok-count">{okCount}</div>
            <div className="text-[10px] text-neutral-400 uppercase">ok</div>
          </div>
          <div className="bg-amber-950/40 border border-amber-500/30 rounded p-2">
            <div className="text-lg font-bold text-amber-300" data-testid="yugioh-img-pending-count">{img.pending || 0}</div>
            <div className="text-[10px] text-neutral-400 uppercase">pending</div>
          </div>
          <div className="bg-red-950/40 border border-red-500/30 rounded p-2">
            <div className="text-lg font-bold text-red-300" data-testid="yugioh-img-failed-count">{img.failed || 0}</div>
            <div className="text-[10px] text-neutral-400 uppercase">failed</div>
          </div>
        </div>
        {img.running && <div className="text-[11px] text-blue-300 mt-3 animate-pulse">⟳ backfill en curso…</div>}
      </div>

      {/* Feb 2026 v30.5 — Toggle del flag `yugioh_enabled` (lo que
          enciende/apaga Yu-Gi-Oh en el catálogo público). Siempre visible,
          color según estado (verde=ON, gris=OFF). Confirmación obligatoria. */}
      <div className={`rounded-md p-4 border ${data?.yugioh_enabled ? "bg-emerald-950/40 border-emerald-500/40" : "bg-neutral-900/40 border-neutral-700"}`} data-testid="yugioh-flag-toggle-card">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-sm font-semibold uppercase tracking-wider mb-1" style={{ color: data?.yugioh_enabled ? "#6ee7b7" : "#a3a3a3" }}>
              Yu-Gi-Oh! en el catálogo público
            </div>
            <div className="text-xs text-neutral-400">
              Estado actual: <span className="font-bold" data-testid="yugioh-flag-state">{data?.yugioh_enabled ? "ACTIVADO (visible al público)" : "DESACTIVADO (invisible)"}</span>
            </div>
            <div className="text-[11px] text-neutral-500 mt-1">
              Reversible al instante — solo cambia la visibilidad, los datos del seed permanecen.
            </div>
          </div>
          <Button
            size="sm"
            onClick={toggleYugiohFlag}
            disabled={busy}
            className={data?.yugioh_enabled ? "bg-neutral-700 hover:bg-neutral-800 text-white" : "bg-emerald-600 hover:bg-emerald-700 text-white"}
            data-testid="yugioh-flag-toggle-btn"
          >
            {data?.yugioh_enabled ? "Desactivar Yu-Gi-Oh!" : "Activar Yu-Gi-Oh!"}
          </Button>
        </div>
      </div>

      {/* Actions */}
      <div className="bg-[#0A0A0A] border border-amber-500/30 rounded-md p-4" data-testid="yugioh-seed-actions">
        <div className="text-sm text-amber-200 font-medium mb-2">Acciones</div>
        <div className="text-xs text-neutral-400 mb-3">
          Prueba chica primero (ej. 50 cartas) → verifica que las imágenes se vean en el proxy → después seed completo. El flag <b>yugioh_enabled</b> sigue en FALSE — las cartas se guardan pero no se ven en el catálogo público.
        </div>
        <div className="flex gap-2 flex-wrap items-end">
          <div>
            <label className="text-[10px] text-neutral-400 uppercase block mb-1">Limit (prueba chica)</label>
            <Input
              type="number"
              min="1"
              max="500"
              value={limit}
              onChange={(e) => setLimit(parseInt(e.target.value) || 50)}
              className="bg-[#171717] border-[#262626] w-24"
              data-testid="yugioh-seed-limit-input"
            />
          </div>
          <Button
            size="sm"
            onClick={() => startSeed(limit)}
            disabled={busy || !r2.configured || data.any_job_running}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="yugioh-seed-test-btn"
          >
            Correr prueba chica
          </Button>
          <Button
            size="sm"
            onClick={() => startSeed(null)}
            disabled={busy || !r2.configured || data.any_job_running}
            className="bg-[#EF4444] hover:bg-[#DC2626]"
            data-testid="yugioh-seed-full-btn"
          >
            Seed completo (~13 000)
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={retryFailed}
            disabled={busy || !r2.configured || (img.failed || 0) === 0}
            className="border-neutral-700 text-neutral-300 hover:bg-neutral-800"
            data-testid="yugioh-seed-retry-btn"
          >
            Reintentar {img.failed || 0} fallidas
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={load}
            className="text-neutral-400 hover:text-white"
            data-testid="yugioh-seed-refresh-btn"
          >
            ⟳ Refresh status
          </Button>
        </div>
      </div>
    </Section>
  );
}


function AllOrders() {
  const [list, setList] = useState([]);
  const [status, setStatus] = useState("");
  const [game, setGame] = useState("all");
  useEffect(() => { api.get("/admin/orders", { params: { ...(status ? { status } : {}), ...(game !== "all" ? { game } : {}) } }).then(r => setList(r.data)); }, [status, game]);
  return (
    <Section title={`Pedidos (${list.length})`} actions={
      <select data-testid="admin-orders-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="bg-[#171717] border border-[#262626] rounded px-3 py-1 text-sm">
        <option value="">Todos</option>
        {["PENDIENTE_PAGO", "PAGO_POR_VERIFICAR", "PAGADO", "ENVIADO", "ENTREGADO", "PENDIENTE_LIBERACION", "LIBERADO", "EN_DISPUTA", "REEMBOLSADO"].map(s => <option key={s}>{s}</option>)}
      </select>
    }>
      <GameTabs value={game} onChange={setGame} testidPrefix="admin-orders" className="mb-3" />
      <div className="bg-[#111] border border-[#262626] rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-neutral-500 text-xs uppercase border-b border-[#262626]"><tr><th className="text-left p-3">ID</th><th className="text-left p-3">Comprador</th><th className="text-left p-3">Vendedor</th><th className="text-left p-3">Estado</th><th className="text-right p-3">Total</th><th></th></tr></thead>
          <tbody>
            {list.map(o => (
              <tr key={o.id} className="border-b border-[#171717]">
                <td className="p-3 font-mono text-xs text-neutral-500">{o.id.slice(0, 8)}</td>
                <td className="p-3">{o.buyer?.name}</td>
                <td className="p-3">{o.seller?.name}</td>
                <td className="p-3"><span className={`status-pill status-${o.status.toLowerCase().replace("_", "-")}`}>{o.status}</span></td>
                <td className="p-3 text-right text-[#EF4444] font-medium">${o.total.toFixed(2)}</td>
                <td className="p-3 text-right"><a href={`/order/${o.id}`} className="text-[#EF4444] hover:underline">Ver</a></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

function Reconciliation() {
  // Feb 2026 tanda 3 — red de seguridad. Muestra órdenes con `reconciliation`
  // registrada, con foco en descuadres. Cero edición desde acá — es solo
  // visibilidad para auditoría manual. Backfill dispara la comparación sobre
  // órdenes históricas que aún no tienen el sub-doc.
  // v2 (Feb 2026 hueco fee-only): el confirm del backfill detecta órdenes v1
  // y ofrece auto-force para re-evaluar con la lógica nueva (fee derivado
  // por método real). Cero duplicación — sub-doc único por orden.
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("mismatch");
  // Feb 2026 — filtro de revisión. Default "pending" para que armonice con
  // el badge del sidebar: al entrar ves lo que te falta revisar.
  const [reviewedFilter, setReviewedFilter] = useState("pending");
  const [backfilling, setBackfilling] = useState(false);
  const [loading, setLoading] = useState(true);
  // Draft por-fila del input de nota — así el usuario tipea sin PATCHear
  // en cada tecla; guarda al blur o Enter.
  const [noteDraft, setNoteDraft] = useState({});
  const load = () => {
    setLoading(true);
    api.get("/admin/reconciliation/mismatches", {
      params: { status: statusFilter, reviewed_filter: reviewedFilter, limit: 100 },
    })
      .then(r => { setList(r.data.orders || []); setTotal(r.data.total || 0); })
      .finally(() => setLoading(false));
  };
  useEffect(load, [statusFilter, reviewedFilter]);
  // PATCH helper — `reviewed` y `note` independientes, ambos opcionales.
  // El backend NUNCA toca campos de dinero (total/net_seller/payphone_fee).
  // Solo escribe en `orders.reconciliation_review` (sub-doc separado de
  // `orders.reconciliation`, que es el que sobrescribe _reconcile_payment).
  const patchReview = async (orderId, patch) => {
    try {
      const r = await api.patch(`/admin/reconciliation/${orderId}`, patch);
      setList((prev) => prev.map(o => o.order_id === orderId
        ? { ...o, reconciliation_review: r.data.reconciliation_review }
        : o));
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || "No pude actualizar la revisión");
      return false;
    }
  };
  const toggleReviewed = async (o) => {
    const currentReviewed = !!(o.reconciliation_review?.reviewed);
    // Si va de pending → revisado y no hay nota aún, ofrecé escribir una.
    // La nota es SIEMPRE opcional — se puede marcar revisado sin escribir nada.
    let note;
    if (!currentReviewed && !(o.reconciliation_review?.note || "").trim()) {
      const proposed = window.prompt("Nota (opcional — déjalo vacío si no quieres escribir nada):", "");
      if (proposed && proposed.trim()) note = proposed.trim();
    }
    const ok = await patchReview(o.order_id, {
      reviewed: !currentReviewed,
      ...(note !== undefined ? { note } : {}),
    });
    if (ok) {
      toast.success(currentReviewed ? "Revisión quitada" : "Marcado como revisado");
      // Si el filtro escondería el item tras el toggle, recargamos la lista.
      if (reviewedFilter !== "all") load();
    }
  };
  const saveNote = async (orderId) => {
    const draft = noteDraft[orderId];
    if (draft === undefined) return; // no hubo cambio local
    const ok = await patchReview(orderId, { note: draft });
    if (ok) {
      setNoteDraft((prev) => { const n = { ...prev }; delete n[orderId]; return n; });
      toast.success("Nota guardada");
    }
  };
  const runBackfill = async () => {
    // Detect legacy v1 rows to auto-offer force mode in the confirm.
    let v1Count = 0;
    try {
      const rc = await api.get("/admin/reconciliation/v1-count");
      v1Count = rc.data?.v1_count || 0;
    } catch { /* fall back to non-force */ }
    let forceReprocess = false;
    let confirmMsg;
    if (v1Count > 0) {
      forceReprocess = true;
      confirmMsg = `Detecté ${v1Count} órdenes con reconciliación vieja (v1 sin fee derivado). Voy a RE-EVALUAR TODAS con la lógica nueva. NO modifica totales, fees ni net_seller — solo sobrescribe el sub-doc de reconciliación. ¿Continuar?`;
    } else {
      confirmMsg = "Correr backfill sobre órdenes históricas sin reconciliación. NO modifica montos — solo agrega el flag. ¿Continuar?";
    }
    if (!window.confirm(confirmMsg)) return;
    setBackfilling(true);
    try {
      const r = await api.post("/admin/reconciliation/backfill", null, {
        params: forceReprocess ? { force_reprocess: true } : {},
      });
      const modeLabel = r.data.force_reprocess ? "FORCE (v2)" : "normal";
      toast.success(`Backfill ${modeLabel} OK — procesadas: ${r.data.processed}, descuadres detectados: ${r.data.marked_mismatch}, saltadas sin data: ${r.data.skipped_no_data}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falló el backfill");
    } finally { setBackfilling(false); }
  };
  const deltaClass = (d) => {
    if (d == null) return "text-neutral-500";
    if (Math.abs(d) <= 0.01) return "text-emerald-500";
    return d < 0 ? "text-red-500 font-bold" : "text-amber-500 font-bold";
  };
  // Impact summary — separated by semantics per user's decision (Feb 2026):
  //  - Fees: sum of delta_derived_fee where mismatch (what HitTCG ate in
  //    unrecorded Payphone fees, or paid to sellers as ghost fees)
  //  - Gross: sum of delta_total where mismatch (rare in EC; only if buyer
  //    was ever charged a different amount than order.total)
  const impactFees = list.reduce((acc, o) => {
    const d = o.reconciliation?.delta_derived_fee;
    return d != null ? acc + d : acc;
  }, 0);
  const impactGross = list.reduce((acc, o) => {
    const d = o.reconciliation?.delta_total;
    return d != null ? acc + d : acc;
  }, 0);
  return (
    <Section
      title={`Descuadres de pago (${total})`}
      actions={
        <div className="flex items-center gap-2">
          <select
            data-testid="reconciliation-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-[#171717] border border-[#262626] rounded px-3 py-1 text-sm"
          >
            <option value="mismatch">Solo descuadres</option>
            <option value="ok">Solo OK</option>
            <option value="all">Todas</option>
          </select>
          <select
            data-testid="reconciliation-reviewed-filter"
            value={reviewedFilter}
            onChange={(e) => setReviewedFilter(e.target.value)}
            className="bg-[#171717] border border-[#262626] rounded px-3 py-1 text-sm"
            title="Estado de revisión del admin (armoniza con el badge del sidebar)"
          >
            <option value="pending">Pendientes de revisar</option>
            <option value="reviewed">Ya revisados</option>
            <option value="all">Todos</option>
          </select>
          <Button
            data-testid="reconciliation-backfill-btn"
            onClick={runBackfill}
            disabled={backfilling}
            variant="outline"
            className="border-[#EF4444] text-[#EF4444]"
          >
            <BusyLabel busy={backfilling} text="Procesando…">Ejecutar backfill histórico</BusyLabel>
          </Button>
        </div>
      }
    >
      <p className="text-xs text-neutral-500 mb-3">
        Muestra órdenes donde el monto o el fee real no coincidió con el esperado. Tolerancia: $0.01. La reconciliación es <b>observadora</b> — jamás modifica totales, fees o net_seller. El pago al vendedor se hace por lo que él vio en su orden, sin importar el descuadre. Marca &quot;Revisado&quot; cuando ya evaluaste el caso — sale del contador rojo del sidebar sin borrarse.
      </p>
      {loading ? (
        <div className="text-neutral-500">Cargando...</div>
      ) : list.length === 0 ? (
        <div data-testid="reconciliation-empty" className="border border-dashed border-[#262626] rounded p-6 text-center text-neutral-500">
          {statusFilter === "mismatch" ? "Cero descuadres. 👌" : "Sin registros con ese filtro."}
        </div>
      ) : (
        <div className="bg-[#111] border border-[#262626] rounded-md overflow-x-auto">
          <table className="w-full text-sm min-w-[1200px]" data-testid="reconciliation-table">
            <thead className="text-neutral-500 text-xs uppercase border-b border-[#262626]">
              <tr>
                <th className="text-left p-2">Verificado</th>
                <th className="text-left p-2">Orden</th>
                <th className="text-left p-2">Método</th>
                <th className="text-right p-2">Total esp.</th>
                <th className="text-right p-2">Total real</th>
                <th className="text-right p-2">Δ Total</th>
                <th className="text-right p-2">Fee guardado</th>
                <th className="text-right p-2">Fee derivado</th>
                <th className="text-right p-2">Δ Fee</th>
                <th className="text-left p-2">Notas</th>
                <th className="text-left p-2">Nota admin</th>
                <th className="text-left p-2">Revisado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {list.map(o => {
                const r = o.reconciliation || {};
                const rv = o.reconciliation_review || {};
                // Formato de fecha compacto: "26/8 16:55" (evita el multiline de toLocaleString).
                const compactDate = (iso) => {
                  if (!iso) return "—";
                  const d = new Date(iso);
                  return `${d.getDate()}/${d.getMonth() + 1} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
                };
                return (
                  <tr key={o.order_id} className={`border-b border-[#171717] ${rv.reviewed ? "opacity-60" : ""}`} data-testid={`reconciliation-row-${o.order_id}`}>
                    <td className="p-2 text-xs text-neutral-400 whitespace-nowrap">{compactDate(r.checked_at)}</td>
                    <td className="p-2 font-mono text-xs text-neutral-500">#{o.order_id.slice(0, 8)}</td>
                    <td className="p-2">
                      <span className={`text-xs px-2 py-0.5 rounded ${r.method_used === "transfer" ? "bg-blue-500/10 text-blue-400" : "bg-purple-500/10 text-purple-400"}`}>
                        {r.method_used === "transfer" ? "Transf." : "Payphone"}
                      </span>
                    </td>
                    <td className="p-2 text-right text-xs">${(r.expected_total ?? 0).toFixed(2)}</td>
                    <td className="p-2 text-right text-xs">${(r.actual_total ?? 0).toFixed(2)}</td>
                    <td className={`p-2 text-right text-xs ${deltaClass(r.delta_total)}`}>
                      {r.delta_total != null ? `${r.delta_total >= 0 ? "+" : ""}${r.delta_total.toFixed(2)}` : "—"}
                    </td>
                    <td className="p-2 text-right text-xs text-neutral-400">${(r.expected_fee ?? 0).toFixed(2)}</td>
                    <td className="p-2 text-right text-xs text-neutral-300">{r.derived_fee != null ? `$${r.derived_fee.toFixed(2)}` : "—"}</td>
                    <td className={`p-2 text-right text-xs ${deltaClass(r.delta_derived_fee)}`}>
                      {r.delta_derived_fee != null ? `${r.delta_derived_fee >= 0 ? "+" : ""}${r.delta_derived_fee.toFixed(2)}` : "—"}
                    </td>
                    <td className="p-2 text-xs text-neutral-400 max-w-[140px] truncate" title={r.notes}>{r.notes}</td>
                    <td className="p-2">
                      <input
                        type="text"
                        data-testid={`reconciliation-note-${o.order_id}`}
                        defaultValue={rv.note || ""}
                        onChange={(e) => setNoteDraft((prev) => ({ ...prev, [o.order_id]: e.target.value }))}
                        onBlur={() => saveNote(o.order_id)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.currentTarget.blur(); } }}
                        placeholder="Nota…"
                        title={rv.note_updated_at ? `Editada ${compactDate(rv.note_updated_at)}` : ""}
                        className="bg-[#0A0A0A] border border-[#262626] rounded px-2 py-1 text-xs w-28 focus:border-[#EF4444] focus:outline-none"
                      />
                    </td>
                    <td className="p-2 whitespace-nowrap">
                      {rv.reviewed ? (
                        <button
                          onClick={() => toggleReviewed(o)}
                          data-testid={`reconciliation-unreview-${o.order_id}`}
                          title={`Revisado por ${rv.reviewed_by_name || "admin"} · ${compactDate(rv.reviewed_at)} · click para quitar`}
                          className="text-xs px-2 py-1 border border-emerald-500/40 text-emerald-400 rounded hover:bg-red-500/10 hover:border-red-500 hover:text-red-400 transition"
                        >
                          ✓ {compactDate(rv.reviewed_at)}
                        </button>
                      ) : (
                        <button
                          onClick={() => toggleReviewed(o)}
                          data-testid={`reconciliation-mark-reviewed-${o.order_id}`}
                          title="Marcar como revisado"
                          className="text-xs px-2 py-1 border border-neutral-600 text-neutral-300 rounded hover:border-emerald-500 hover:text-emerald-400 hover:bg-emerald-500/10 transition"
                        >
                          Marcar
                        </button>
                      )}
                    </td>
                    <td className="p-2 text-right">
                      <a href={`/order/${o.order_id}`} className="text-[#EF4444] hover:underline text-xs" data-testid={`reconciliation-view-${o.order_id}`}>Ver</a>
                    </td>
                  </tr>
                );
              })}
              {statusFilter === "mismatch" && list.length > 0 && (
                <>
                  <tr className="border-t-2 border-[#262626] bg-[#0A0A0A]" data-testid="reconciliation-impact-fees">
                    <td colSpan={8} className="p-3 text-right text-xs uppercase tracking-wider text-neutral-400">Impacto en fees ({list.length} órdenes)</td>
                    <td className={`p-3 text-right font-bold ${impactFees < 0 ? "text-red-500" : impactFees > 0 ? "text-emerald-500" : "text-neutral-400"}`}>
                      {impactFees >= 0 ? "+" : ""}${impactFees.toFixed(2)}
                    </td>
                    <td colSpan={4} className="p-3 text-[10px] text-neutral-500">HitTCG comió (negativo) o vendedor perdió (positivo)</td>
                  </tr>
                  <tr className="bg-[#0A0A0A]" data-testid="reconciliation-impact-gross">
                    <td colSpan={5} className="p-3 text-right text-xs uppercase tracking-wider text-neutral-400">Impacto en gross</td>
                    <td className={`p-3 text-right font-bold ${impactGross < 0 ? "text-red-500" : impactGross > 0 ? "text-emerald-500" : "text-neutral-400"}`}>
                      {impactGross >= 0 ? "+" : ""}${impactGross.toFixed(2)}
                    </td>
                    <td colSpan={7} className="p-3 text-[10px] text-neutral-500">Diferencia entre lo que el buyer pagó y lo que la orden dice</td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

function ShippingAudit() {
  // Feb 2026 — reporte OBSERVADOR. Lista órdenes donde el `net_seller`
  // guardado en la base fue calculado con la fórmula pre-Feb-2026 (envío
  // afuera del ledger), así que al vendedor le falta cobrar el envío.
  // Regla innegociable: cero mutación. Solo lee y muestra. El pago del
  // delta al vendedor se hace MANUAL, aparte, fuera del ledger.
  const [data, setData] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);
  const load = () => {
    setLoading(true);
    api.get("/admin/orders/shipping-missing-report", { params: { status_filter: statusFilter } })
      .then(r => setData(r.data))
      .catch(() => toast.error("No pude cargar el reporte"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [statusFilter]);
  const copyWhatsapp = async (row) => {
    const short = row.order_id.slice(0, 8);
    const delta = Number(row.delta_owed).toFixed(2);
    const msg = `Hola ${row.seller?.name || ""}, revisando el sistema encontré que en tu pedido #${short} nos faltó pagarte el envío ($${delta}). Te lo transferimos aparte hoy — pásanos por favor los datos de tu cuenta para depositarte. Gracias por la paciencia.`;
    try {
      await navigator.clipboard.writeText(msg);
      setCopiedId(row.order_id);
      toast.success("Mensaje copiado");
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      toast.error("No pude copiar al portapapeles");
    }
  };
  const summary = data?.summary || { total_orders: 0, total_debt_usd: 0, unpaid: { count: 0, debt: 0 }, paid: { count: 0, debt: 0 } };
  const orders = data?.orders || [];
  const unpaidRows = orders.filter(r => !r.already_released_to_seller);
  const paidRows   = orders.filter(r => r.already_released_to_seller);
  return (
    <Section
      title="Auditoría de envíos"
      actions={
        <div className="flex items-center gap-2">
          <select
            data-testid="shipping-audit-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-[#171717] border border-[#262626] text-sm px-3 py-1.5 rounded text-white"
          >
            <option value="all">Todas</option>
            <option value="unpaid">Pendientes de liberar</option>
            <option value="paid">Ya pagadas al vendedor</option>
          </select>
          <Button
            data-testid="shipping-audit-reload"
            onClick={load}
            variant="outline"
            size="sm"
            className="border-[#262626] text-neutral-300 hover:bg-[#262626]"
          >Recargar</Button>
        </div>
      }
    >
      <p className="text-xs text-neutral-500 mb-4 leading-relaxed">
        Lista órdenes donde el <b>net_seller</b> guardado en la base fue calculado con la fórmula pre-Feb-2026 (envío afuera del ledger). Reporte <b className="text-amber-300">observador</b> — jamás modifica <code className="text-neutral-400">net_seller</code>, <code className="text-neutral-400">total</code> ni ningún campo de dinero. Solo lee y muestra la deuda de envío pendiente para pagar <b>manualmente aparte</b> a cada vendedor. Excluye órdenes con envío $0 y las canceladas/reembolsadas.
      </p>

      {/* Totales */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6" data-testid="shipping-audit-totals">
        <div className="bg-[#0A0A0A] border border-[#262626] rounded p-4">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1">Deuda total a vendedores</div>
          <div className="font-display text-3xl text-[#EF4444]" data-testid="shipping-audit-total-debt">${summary.total_debt_usd.toFixed(2)}</div>
          <div className="text-xs text-neutral-500 mt-1">{summary.total_orders} orden{summary.total_orders === 1 ? "" : "es"} afectada{summary.total_orders === 1 ? "" : "s"}</div>
        </div>
        <div className="bg-[#0A0A0A] border border-amber-500/30 rounded p-4">
          <div className="text-[10px] uppercase tracking-wider text-amber-400 mb-1">Pendientes de liberar</div>
          <div className="font-display text-2xl text-amber-300" data-testid="shipping-audit-unpaid-debt">${summary.unpaid.debt.toFixed(2)}</div>
          <div className="text-xs text-neutral-500 mt-1">{summary.unpaid.count} orden{summary.unpaid.count === 1 ? "" : "es"} — se puede corregir antes de pagar</div>
        </div>
        <div className="bg-[#0A0A0A] border border-red-500/30 rounded p-4">
          <div className="text-[10px] uppercase tracking-wider text-red-400 mb-1">Ya pagadas al vendedor</div>
          <div className="font-display text-2xl text-red-300" data-testid="shipping-audit-paid-debt">${summary.paid.debt.toFixed(2)}</div>
          <div className="text-xs text-neutral-500 mt-1">{summary.paid.count} orden{summary.paid.count === 1 ? "" : "es"} — hay que pagar aparte</div>
        </div>
      </div>

      {loading ? (
        <div className="text-neutral-500">Cargando…</div>
      ) : orders.length === 0 ? (
        <div data-testid="shipping-audit-empty" className="border border-dashed border-[#262626] rounded p-8 text-center text-neutral-500">
          <Truck size={32} className="mx-auto mb-2 opacity-50" />
          Todo cuadra. Ninguna orden con envío faltante en el <code>net_seller</code>.
        </div>
      ) : (
        <div className="space-y-8">
          {unpaidRows.length > 0 && (statusFilter !== "paid") && (
            <ShippingAuditGroup
              title="Pendientes de liberar"
              subtitle="Se puede corregir el envío ANTES de pagar al vendedor — se lo sumas al depósito."
              rows={unpaidRows}
              tone="amber"
              onCopy={copyWhatsapp}
              copiedId={copiedId}
            />
          )}
          {paidRows.length > 0 && (statusFilter !== "unpaid") && (
            <ShippingAuditGroup
              title="Ya pagadas al vendedor"
              subtitle="Estas ya se liberaron con el envío afuera. Hay que pagarle el delta APARTE al vendedor, fuera del ledger."
              rows={paidRows}
              tone="red"
              onCopy={copyWhatsapp}
              copiedId={copiedId}
            />
          )}
        </div>
      )}
    </Section>
  );
}

function ShippingAuditGroup({ title, subtitle, rows, tone, onCopy, copiedId }) {
  const border = tone === "amber" ? "border-amber-500/30" : "border-red-500/30";
  const badge  = tone === "amber" ? "bg-amber-500/20 text-amber-300" : "bg-red-500/20 text-red-300";
  const sumDelta = rows.reduce((acc, r) => acc + Number(r.delta_owed || 0), 0);
  return (
    <div className={`border ${border} rounded-md`} data-testid={`shipping-audit-group-${tone}`}>
      <div className="p-4 border-b border-[#262626] flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="flex items-center gap-2">
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${badge}`}>{title}</span>
            <span className="text-sm text-neutral-400">{rows.length} orden{rows.length === 1 ? "" : "es"}</span>
          </div>
          <p className="text-xs text-neutral-500 mt-1">{subtitle}</p>
        </div>
        <div className="font-mono text-sm text-white">Subtotal deuda: <b>${sumDelta.toFixed(2)}</b></div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[1100px]">
          <thead className="bg-[#0A0A0A] text-[10px] uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="text-left px-3 py-2">Orden</th>
              <th className="text-left px-3 py-2">Vendedor</th>
              <th className="text-right px-3 py-2">Precio</th>
              <th className="text-right px-3 py-2">Envío</th>
              <th className="text-right px-3 py-2">Comisión</th>
              <th className="text-right px-3 py-2">Payphone</th>
              <th className="text-right px-3 py-2">Neto guardado</th>
              <th className="text-right px-3 py-2">Neto correcto</th>
              <th className="text-right px-3 py-2 text-[#EF4444]">Delta que se debe</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.order_id} className="border-b border-[#171717]" data-testid={`shipping-audit-row-${r.order_id}`}>
                <td className="px-3 py-2 font-mono text-xs">
                  <a href={`/order/${r.order_id}`} target="_blank" rel="noreferrer" className="text-[#EF4444] hover:underline" data-testid={`shipping-audit-open-${r.order_id}`}>
                    #{r.order_id.slice(0, 8)}
                  </a>
                  <div className="text-neutral-500 text-[10px]">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}</div>
                  {r.bundle_id && <div className="text-neutral-600 text-[10px]">Carrito · {r.item_count || 1} carta{(r.item_count || 1) === 1 ? "" : "s"}</div>}
                </td>
                <td className="px-3 py-2">
                  <div className="text-white">{r.seller?.name || "—"}</div>
                  <div className="text-neutral-500 text-[10px]">{r.seller?.email || ""}</div>
                  {r.buyer_name && <div className="text-neutral-600 text-[10px]">Comprador: {r.buyer_name}</div>}
                </td>
                <td className="px-3 py-2 text-right font-mono text-white">${r.price.toFixed(2)}</td>
                <td className="px-3 py-2 text-right font-mono text-white">${r.shipping_cost.toFixed(2)}</td>
                <td className="px-3 py-2 text-right font-mono text-neutral-400">-${r.commission.toFixed(2)}</td>
                <td className="px-3 py-2 text-right font-mono text-neutral-400">-${r.payphone_fee.toFixed(2)}</td>
                <td className="px-3 py-2 text-right font-mono text-neutral-500 line-through">${r.net_seller_stored.toFixed(2)}</td>
                <td className="px-3 py-2 text-right font-mono text-white">${r.net_seller_correct.toFixed(2)}</td>
                <td className="px-3 py-2 text-right font-mono font-bold text-[#EF4444]" data-testid={`shipping-audit-delta-${r.order_id}`}>+${r.delta_owed.toFixed(2)}</td>
                <td className="px-3 py-2 text-right">
                  <Button
                    onClick={() => onCopy(r)}
                    size="sm"
                    variant="outline"
                    className="border-[#262626] text-neutral-300 hover:bg-[#262626] text-xs"
                    data-testid={`shipping-audit-copy-${r.order_id}`}
                  >
                    {copiedId === r.order_id ? <><Check size={12} className="mr-1" />Copiado</> : <><Copy size={12} className="mr-1" />WhatsApp</>}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}



function Disputes() {
  const [list, setList] = useState([]);
  const [game, setGame] = useState("all");
  useEffect(() => { api.get("/admin/disputes", { params: game !== "all" ? { game } : {} }).then(r => setList(r.data)); }, [game]);
  return (
    <Section title={`Disputas abiertas (${list.length})`}>
      <GameTabs value={game} onChange={setGame} testidPrefix="admin-disputes" className="mb-3" />
      {list.length === 0 && <div className="text-neutral-500">Sin disputas activas.</div>}
      <div className="space-y-3">
        {list.map(o => (
          <a key={o.id} href={`/order/${o.id}`} className="block bg-[#111] border border-[#EF4444]/30 rounded-md p-4 hover:border-[#EF4444]">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">Caso {o.dispute?.case} — {cardName(o.card, o.publication_language)}</div>
                <div className="text-sm text-neutral-400 mt-1 line-clamp-1">{o.dispute?.reason}</div>
                <div className="text-xs text-neutral-500 mt-2">Comprador: {o.buyer?.name} · Vendedor: {o.seller?.name}</div>
              </div>
              <div className="text-right">
                <div className="font-display text-2xl text-[#EF4444]">${o.total.toFixed(2)}</div>
                <div className="text-xs text-neutral-500">Ver detalle →</div>
              </div>
            </div>
          </a>
        ))}
      </div>
    </Section>
  );
}

function PubReview() {
  const [list, setList] = useState([]);
  const [detail, setDetail] = useState(null);
  const [editCard, setEditCard] = useState(null);
  const [editPub, setEditPub] = useState(null);
  const [uploadingRef, setUploadingRef] = useState(false);
  const [game, setGame] = useState("all");
  const { prompt } = useDialog();
  const { open } = useLightbox();
  const load = () => api.get("/admin/publications/review", { params: game !== "all" ? { game } : {} }).then(r => setList(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [game]);
  const refreshDetail = async (pubId) => {
    const r = await api.get("/admin/publications/review", { params: game !== "all" ? { game } : {} });
    setList(r.data);
    const updated = r.data.find(p => p.id === pubId);
    if (updated) setDetail(updated);
  };
  const approve = async (id) => {
    try {
      await api.post(`/admin/publications/${id}/approve`);
      toast.success("Aprobada");
      setDetail(null); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo aprobar");
    }
  };
  const reject = async (id) => {
    const reason = await prompt({ title: "Rechazar publicación", label: "Motivo (el vendedor lo verá)", multiline: true, required: true });
    if (!reason) return;
    await api.post(`/admin/publications/${id}/reject`, { reason }); toast.success("Rechazada"); setDetail(null); load();
  };
  const saveCard = async () => {
    if (!editCard) return;
    try {
      await api.put(`/admin/custom-cards/${editCard.id}`, {
        name_es: editCard.name_es, set_name: editCard.set_name, serie_name: editCard.serie_name,
        number: editCard.number, custom_notes: editCard.custom_notes,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo guardar");
      return;
    }
    toast.success("Carta editada");
    setEditCard(null);
    if (detail) await refreshDetail(detail.id);
  };
  const uploadReferenceImage = async (file) => {
    if (!editCard || !file) return;
    setUploadingRef(true);
    try {
      const path = await uploadPublic(file);
      await api.post(`/admin/custom-cards/${editCard.id}/set-image`, { photo_path: path });
      toast.success("Foto de referencia subida");
      setEditCard(c => ({ ...c, image_url: path }));
      if (detail) await refreshDetail(detail.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo subir la imagen");
    } finally {
      setUploadingRef(false);
    }
  };
  const savePub = async () => {
    if (!editPub) return;
    const body = {
      language: editPub.language,
      language_other: editPub.language_other || "",
      condition: editPub.condition,
      variant: editPub.variant,
      price: parseFloat(editPub.price),
      quantity: parseInt(editPub.quantity),
      ship_cost: parseFloat(editPub.ship_cost),
    };
    try {
      await api.put(`/admin/publications/${editPub.id}`, body);
      toast.success("Publicación editada");
      setEditPub(null);
      if (detail) await refreshDetail(detail.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo guardar");
    }
  };

  return (
    <Section title={`Publicaciones en revisión (${list.length})`}>
      {list.length === 0 && <div className="text-neutral-500">Sin cola.</div>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {list.map(p => {
          const cardImg = p.card?.image_url;
          const idPhoto = p.card?.identification_photos?.[0];
          const isCustom = !!p.card?.is_custom;
          const missingRef = isCustom && !cardImg;
          const sealed = isSealed(p);
          const thumbSrc = cardImg
            ? (cardImg.startsWith("http") ? cardImg
               : cardImg.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${cardImg}`
               : `${process.env.REACT_APP_BACKEND_URL}/api/files/public/${cardImg}`)
            : (idPhoto ? `${process.env.REACT_APP_BACKEND_URL}/api/files/public/${idPhoto}` : null);
          return (
          <div key={p.id} data-testid={`pub-review-${p.id}`} className={`bg-[#111] border rounded-md p-4 border-l-2 ${missingRef ? "border-amber-500/50 border-l-amber-500" : sealed ? "border-purple-500/50 border-l-purple-500" : "border-[#262626] border-l-transparent"}`}>
            <div className="flex gap-3">
              <div className={`w-16 h-20 bg-[#0A0A0A] rounded overflow-hidden shrink-0 relative flex items-center justify-center border ${sealed ? "border-purple-500/60" : "border-transparent"}`}>
                {thumbSrc ? (
                  <img src={thumbSrc} alt="" className={`w-full h-full object-contain ${!cardImg ? "opacity-70" : ""}`} onError={(e) => e.currentTarget.style.display = "none"} />
                ) : (
                  <PackageX size={20} className="text-neutral-700" />
                )}
                {missingRef && idPhoto && (
                  <div className="absolute bottom-0 inset-x-0 bg-amber-500/90 text-black text-[8px] font-bold text-center leading-tight py-0.5">ID</div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <div className="text-sm font-medium line-clamp-1 flex-1 min-w-0">{cardName(p.card, p.language)}</div>
                  {sealed && <span className={sealedPillClass} data-testid={`pub-review-sealed-${p.id}`}>📦 {sealedLabel(p)}</span>}
                </div>
                <div className="text-xs text-neutral-500">{p.language.toUpperCase()} · {p.variant || "normal"} · {p.condition} · ${p.price}</div>
                <div className="text-xs text-neutral-500">Vendedor: {p.seller?.name}</div>
                {/* Graded certification chip — surfaces in the list so the admin
                    knows AT A GLANCE which items need slab verification before
                    approving. The detail view shows the full breakdown. */}
                {p.graded && (
                  <div className="mt-1 inline-flex items-center gap-1 bg-purple-600/95 text-white text-[10px] px-2 py-0.5 rounded font-bold border border-purple-300/40" data-testid={`pub-review-graded-chip-${p.id}`}>
                    🏆 {p.graded_house} {p.graded_grade}
                  </div>
                )}
                <div className="text-xs text-amber-400 mt-1">{p.review_reason}</div>
                {missingRef && (
                  <div className="mt-2 p-2 bg-amber-500/10 border border-amber-500/40 rounded text-[11px] text-amber-200 leading-snug">
                    <b>Carta custom sin foto de referencia oficial.</b> Aprobar está bloqueado hasta que subas una imagen limpia del catálogo. {idPhoto ? "El thumbnail muestra la foto de identificación del vendedor." : "El vendedor no envió fotos de identificación."}
                  </div>
                )}
              </div>
            </div>
            <div className="mt-3 flex gap-2 flex-wrap">
              <Button data-testid={`pub-review-detail-${p.id}`} size="sm" onClick={() => setDetail(p)} className="bg-[#EF4444] hover:bg-[#DC2626]">Ver detalle</Button>
              {missingRef ? (
                <Button size="sm" onClick={() => { setDetail(p); setEditCard({ ...p.card }); }} className="bg-amber-600 hover:bg-amber-700" data-testid={`pub-review-fix-ref-${p.id}`}>
                  Subir foto de referencia
                </Button>
              ) : (
                <Button size="sm" onClick={() => approve(p.id)} className="bg-emerald-600 hover:bg-emerald-700">Aprobar</Button>
              )}
              <Button size="sm" variant="outline" onClick={() => reject(p.id)} className="border-[#EF4444] text-[#EF4444]">Rechazar</Button>
            </div>
          </div>
        );})}
      </div>

      {detail && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))]" onClick={() => setDetail(null)}>
          <div className="bg-[#111] border border-[#262626] rounded-md p-6 max-w-3xl w-full max-h-[calc(100dvh-4rem)] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-4">
              <h3 className="font-display text-2xl">{cardName(detail.card, detail.publication_language || detail.language)}</h3>
              <button onClick={() => setDetail(null)} className="text-neutral-500 hover:text-white"><X size={20} /></button>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm mb-4">
              <div><b className="text-neutral-400">Set:</b> {detail.card?.set_name}</div>
              <div><b className="text-neutral-400">Núm:</b> {detail.card?.number}</div>
              <div><b className="text-neutral-400">Idioma:</b> {detail.language.toUpperCase()}</div>
              <div><b className="text-neutral-400">Variante:</b> {detail.variant || "normal"}</div>
              <div><b className="text-neutral-400">Condición:</b> {detail.condition}</div>
              <div><b className="text-neutral-400">Precio:</b> ${detail.price} (neto)</div>
              <div><b className="text-neutral-400">Cantidad:</b> {detail.quantity}</div>
              <div><b className="text-neutral-400">Envío:</b> ${detail.ship_cost}</div>
              <div className="col-span-2"><b className="text-neutral-400">Vendedor:</b> {detail.seller?.name} ({detail.seller?.email})</div>
            </div>
            <div className="mb-4">
              <div className="text-xs text-neutral-500 uppercase mb-2">Fotos reales (click para ampliar)</div>
              <div className="grid grid-cols-4 gap-2">
                {detail.photos?.map((p) => (
                  <button key={p} type="button" onClick={() => open(detail.photos.map(x => ({ path: x, scope: "public" })), detail.photos.indexOf(p))}>
                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/public/${p}`} alt="" className="w-full aspect-[3/4] object-cover rounded border border-[#262626] cursor-pointer hover:border-[#EF4444]" onError={(e) => e.currentTarget.style.display = "none"} />
                  </button>
                ))}
              </div>
            </div>
            {detail.card?.is_custom && (
              <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded space-y-3">
                <div className="text-sm text-amber-300">
                  Esta es una carta personalizada (no está en el catálogo automático). Usa las fotos del vendedor para identificarla,
                  edita los datos si es necesario y sube la <b>foto de referencia limpia</b> antes de aprobar.
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-20 h-28 bg-[#0A0A0A] rounded overflow-hidden border border-[#262626] shrink-0 flex items-center justify-center">
                    {detail.card?.image_url ? (
                      <img
                        src={detail.card.image_url.startsWith("http") ? detail.card.image_url
                             : detail.card.image_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${detail.card.image_url}`
                             : `${process.env.REACT_APP_BACKEND_URL}/api/files/public/${detail.card.image_url}`}
                        alt="ref"
                        className="w-full h-full object-contain"
                        data-testid="ref-image-preview"
                      />
                    ) : (
                      <span className="text-[10px] text-neutral-600 text-center px-1">Sin foto de referencia</span>
                    )}
                  </div>
                  <div className="flex-1 text-xs text-neutral-400 space-y-1">
                    <div><b className="text-neutral-300">Serie:</b> {detail.card?.serie_name || "—"}</div>
                    <div><b className="text-neutral-300">Nº:</b> {detail.card?.number || "—"}</div>
                    {detail.card?.custom_notes && <div><b className="text-neutral-300">Notas vendedor:</b> {detail.card.custom_notes}</div>}
                  </div>
                </div>
                <Button size="sm" onClick={() => setEditCard({ ...detail.card })} className="bg-amber-600 hover:bg-amber-700" data-testid="edit-custom-card">
                  Editar carta y subir foto de referencia
                </Button>
              </div>
            )}
            <div className="mb-4 p-3 bg-[#0A0A0A] border border-[#262626] rounded">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs text-neutral-500 uppercase tracking-wider">Datos de la publicación</div>
                <Button size="sm" variant="outline" onClick={() => setEditPub({ ...detail })} className="border-[#262626]" data-testid="edit-pub-btn">
                  Editar publicación
                </Button>
              </div>
              <div className="text-xs text-neutral-500">El admin puede corregir idioma/variante/condición/precio/cantidad sin sacar la publicación de revisión.</div>
            </div>
            {/* GRADED CERTIFICATION PANEL — critical for slab verification.
                Shown ONLY when the seller declared graded=true. The admin must
                compare what's declared here with the slab photos above BEFORE
                approving; if the slab shows a different grade/house or it's
                not visible in any photo, reject with a clear reason. */}
            {detail.graded && (
              <div className="mb-4 p-4 bg-purple-500/10 border-2 border-purple-500/50 rounded" data-testid="pub-review-graded-panel">
                <div className="flex items-center gap-2 mb-3">
                  <span className="bg-purple-600 text-white text-xs font-bold px-2 py-0.5 rounded uppercase tracking-wider">🏆 Carta Gradeada</span>
                  <span className="text-xs text-purple-200">Verifica el slab en las fotos antes de aprobar</span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-[10px] text-purple-300/80 uppercase tracking-wider">Casa gradeadora declarada</div>
                    <div className="font-display text-2xl text-white mt-1" data-testid="pub-review-graded-house">{detail.graded_house || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-purple-300/80 uppercase tracking-wider">Grado declarado</div>
                    <div className="font-display text-2xl text-white mt-1" data-testid="pub-review-graded-grade">{detail.graded_grade || "—"}</div>
                  </div>
                </div>
                <div className="mt-3 p-3 bg-black/40 rounded border border-purple-500/30 text-[11px] text-purple-100 leading-relaxed">
                  <b>Checklist admin:</b> ¿Se ve el slab completo en alguna foto? ¿La etiqueta muestra <b>{detail.graded_house} {detail.graded_grade}</b>? ¿El número de certificación es legible? ¿La carta dentro del slab coincide con el nombre &quot;{cardName(detail.card, detail.publication_language || detail.language)}&quot;? Si algo no cuadra, rechaza con motivo.
                </div>
              </div>
            )}
            <div className="flex gap-2 pt-4 border-t border-[#262626]">
              <Button onClick={() => approve(detail.id)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="detail-approve">Aprobar publicación</Button>
              <Button variant="outline" onClick={() => reject(detail.id)} className="border-[#EF4444] text-[#EF4444]" data-testid="detail-reject">Rechazar con motivo</Button>
            </div>
          </div>
        </div>
      )}

      {editCard && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))]" onClick={() => setEditCard(null)}>
          <div className="bg-[#111] border border-[#262626] rounded-md p-6 max-w-md w-full max-h-[calc(100dvh-4rem)] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-xl mb-4">Editar carta personalizada</h3>
            <div className="space-y-3">
              <div><Label className="text-xs text-neutral-400">Nombre</Label><Input data-testid="edit-card-name" value={editCard.name_es || ""} onChange={(e) => setEditCard(c => ({ ...c, name_es: e.target.value }))} className="bg-[#171717] border-[#262626]" /></div>
              <div><Label className="text-xs text-neutral-400">Set / Edición</Label><Input data-testid="edit-card-set" value={editCard.set_name || ""} onChange={(e) => setEditCard(c => ({ ...c, set_name: e.target.value }))} className="bg-[#171717] border-[#262626]" /></div>
              <div><Label className="text-xs text-neutral-400">Serie</Label><Input data-testid="edit-card-serie" value={editCard.serie_name || ""} onChange={(e) => setEditCard(c => ({ ...c, serie_name: e.target.value }))} placeholder="Ej. Escarlata y Púrpura" className="bg-[#171717] border-[#262626]" /></div>
              <div><Label className="text-xs text-neutral-400">Número de colección</Label><Input value={editCard.number || ""} onChange={(e) => setEditCard(c => ({ ...c, number: e.target.value }))} className="bg-[#171717] border-[#262626]" /></div>
              <div><Label className="text-xs text-neutral-400">Notas</Label><Input value={editCard.custom_notes || ""} onChange={(e) => setEditCard(c => ({ ...c, custom_notes: e.target.value }))} className="bg-[#171717] border-[#262626]" /></div>
              <div className="border-t border-[#262626] pt-3">
                <Label className="text-xs text-neutral-400 block mb-2">Foto de referencia (queda ligada a la CARTA, no a la publicación)</Label>
                {editCard.image_url ? (
                  <div className="flex items-start gap-3 mb-2">
                    <img
                      src={editCard.image_url.startsWith("http") ? editCard.image_url
                           : editCard.image_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${editCard.image_url}`
                           : `${process.env.REACT_APP_BACKEND_URL}/api/files/public/${editCard.image_url}`}
                      alt="ref actual"
                      className="w-24 h-32 object-contain bg-[#0A0A0A] rounded border border-[#262626]"
                      data-testid="edit-card-ref-preview"
                    />
                    <div className="text-xs text-neutral-500 flex-1">
                      Reemplazar la foto de referencia subiendo otra:
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-amber-300 mb-2">Aún no hay foto de referencia. Sube una imagen limpia (idealmente scan oficial).</div>
                )}
                <label
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded border cursor-pointer ${uploadingRef ? "border-neutral-700 text-neutral-500" : "border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10"}`}
                  data-testid="edit-card-upload-ref"
                >
                  <Upload size={14} />
                  <BusyLabel busy={uploadingRef} text="Subiendo…">Subir foto de referencia</BusyLabel>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={uploadingRef}
                    onChange={(e) => { if (e.target.files?.[0]) uploadReferenceImage(e.target.files[0]); e.target.value = ""; }}
                  />
                </label>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={saveCard} className="bg-[#EF4444] hover:bg-[#DC2626]" data-testid="edit-card-save">Guardar datos</Button>
              <Button variant="ghost" onClick={() => setEditCard(null)}>Cerrar</Button>
            </div>
          </div>
        </div>
      )}

      {editPub && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))]" onClick={() => setEditPub(null)}>
          <div className="bg-[#111] border border-[#262626] rounded-md p-6 max-w-md w-full max-h-[calc(100dvh-4rem)] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-xl mb-4">Editar datos de publicación</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-neutral-400">Idioma</Label>
                <select
                  data-testid="edit-pub-language"
                  value={editPub.language || "es"}
                  onChange={(e) => setEditPub(p => ({ ...p, language: e.target.value }))}
                  className="w-full bg-[#171717] border border-[#262626] rounded px-2 py-2 text-sm"
                >
                  <option value="es">Español</option>
                  <option value="en">Inglés</option>
                  <option value="ja">Japonés</option>
                  <option value="otro">Otro</option>
                </select>
              </div>
              <div>
                <Label className="text-xs text-neutral-400">Variante</Label>
                <select
                  data-testid="edit-pub-variant"
                  value={editPub.variant || "normal"}
                  onChange={(e) => setEditPub(p => ({ ...p, variant: e.target.value }))}
                  className="w-full bg-[#171717] border border-[#262626] rounded px-2 py-2 text-sm"
                >
                  {/* Variantes según juego de la carta — mismo helper que
                      SellerDashboard y "Publicaciones (todas)". */}
                  {variantsForCard(editPub.card, editPub.variant).map(v => (
                    <option key={v.value} value={v.value}>{v.label}</option>
                  ))}
                </select>
              </div>
              {!isSealed(editPub) && (
                <div className="col-span-2">
                  <Label className="text-xs text-neutral-400">Condición</Label>
                  <Select value={editPub.condition || "NM"} onValueChange={(v) => setEditPub(p => ({ ...p, condition: v }))}>
                    <SelectTrigger data-testid="edit-pub-condition" className="bg-[#171717] border-[#262626]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#171717] border-[#262626] text-white">
                      {conditionOptions(isLotProduct(editPub.card || editPub) || editPub.condition === "MIXED").map(o => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-xs text-neutral-400">Precio (neto)</Label>
                <Input data-testid="edit-pub-price" type="number" step="0.01" value={editPub.price} onChange={(e) => setEditPub(p => ({ ...p, price: e.target.value }))} className="bg-[#171717] border-[#262626]" />
              </div>
              <div>
                <Label className="text-xs text-neutral-400">Cantidad</Label>
                <Input data-testid="edit-pub-quantity" type="number" value={editPub.quantity} onChange={(e) => setEditPub(p => ({ ...p, quantity: e.target.value }))} className="bg-[#171717] border-[#262626]" />
              </div>
              <div>
                <Label className="text-xs text-neutral-400">Costo envío</Label>
                <Input data-testid="edit-pub-ship" type="number" step="0.01" value={editPub.ship_cost} onChange={(e) => setEditPub(p => ({ ...p, ship_cost: e.target.value }))} className="bg-[#171717] border-[#262626]" />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={savePub} className="bg-[#EF4444] hover:bg-[#DC2626]" data-testid="edit-pub-save">Guardar</Button>
              <Button variant="ghost" onClick={() => setEditPub(null)}>Cancelar</Button>
            </div>
          </div>
        </div>
      )}
    </Section>
  );
}

function Reports() {
  const [list, setList] = useState([]);
  const [game, setGame] = useState("all");
  const load = () => api.get("/admin/reports", { params: game !== "all" ? { game } : {} }).then(r => setList(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [game]);
  const close = async (id) => { await api.post(`/admin/reports/${id}/close`); load(); };
  return (
    <Section title={`Reportes abiertos (${list.length})`}>
      <GameTabs value={game} onChange={setGame} testidPrefix="admin-reports" className="mb-3" />
      {list.length === 0 && <div className="text-neutral-500">Sin reportes.</div>}
      <div className="space-y-2">
        {list.map(r => (
          <div key={r.id} className="bg-[#111] border border-[#262626] rounded-md p-3 flex items-center justify-between">
            <div>
              <div className="text-sm">{r.target_type} · <code className="text-xs text-neutral-500">{r.target_id.slice(0, 8)}</code></div>
              <div className="text-neutral-400 text-sm">{r.reason}</div>
              <div className="text-xs text-neutral-500">{new Date(r.created_at).toLocaleString("es-EC")}</div>
            </div>
            <Button size="sm" variant="ghost" onClick={() => close(r.id)} className="text-neutral-400">Cerrar</Button>
          </div>
        ))}
      </div>
    </Section>
  );
}

// Human-readable label + color for every verification_status value the backend emits.
const VERIF_LABEL = {
  aprobado:              { text: "verificado",       cls: "text-emerald-400" },
  pendiente_revision:    { text: "docs en revisión", cls: "text-blue-400" },
  pendiente_documentos:  { text: "SIN VERIFICAR",    cls: "text-amber-400 font-semibold bg-amber-500/10 border border-amber-500/30 px-2 py-0.5 rounded text-[11px] whitespace-nowrap inline-block" },
  pendiente:             { text: "pendiente",        cls: "text-amber-400" },
  rechazado:             { text: "rechazado",        cls: "text-red-400 font-semibold" },
};

function UsersList() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("all"); // all | unverified | verified | banned
  const [query, setQuery] = useState("");
  const { prompt, confirm } = useDialog();
  const [banTarget, setBanTarget] = useState(null);
  const [strikesUser, setStrikesUser] = useState(null);
  const [unbanTarget, setUnbanTarget] = useState(null);
  const [infoUser, setInfoUser] = useState(null);
  const load = () => api.get("/admin/users").then(r => setList(r.data));
  useEffect(() => { load(); }, []);

  const counts = useMemo(() => ({
    all: list.length,
    unverified: list.filter(u => u.verification_status !== "aprobado" && !u.banned && u.role !== "admin").length,
    verified: list.filter(u => u.verification_status === "aprobado").length,
    banned: list.filter(u => u.banned).length,
  }), [list]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return list.filter(u => {
      if (filter === "unverified" && (u.verification_status === "aprobado" || u.banned)) return false;
      if (filter === "verified" && u.verification_status !== "aprobado") return false;
      if (filter === "banned" && !u.banned) return false;
      if (!q) return true;
      return (u.name || "").toLowerCase().includes(q)
        || (u.email || "").toLowerCase().includes(q)
        || (u.phone || "").includes(q)
        || (u.cedula || "").includes(q);
    });
  }, [list, filter, query]);
  const strike = async (uid) => {
    const reason = await prompt({ title: "Aplicar strike", label: "Motivo", required: true });
    if (!reason) return;
    await api.post("/admin/strikes", { user_id: uid, reason }); toast.success("Strike aplicado"); load();
  };
  const submitBan = async (reason) => {
    try {
      await api.post("/admin/ban", { user_id: banTarget.id, reason });
      toast.success(`Usuario baneado. Motivo enviado por correo a ${banTarget.email}.`);
      setBanTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo banear");
    }
  };
  const submitUnban = async (reason) => {
    try {
      const r = await api.post("/admin/unban", { user_id: unbanTarget.id, reason, remove_from_blacklist: true });
      toast.success(`Usuario desbaneado${r.data.removed_from_blacklist ? " y liberado de lista negra" : ""}. Motivo enviado por correo.`);
      setUnbanTarget(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo desbanear");
    }
  };
  const resetVerif = async (uid) => {
    const reason = await prompt({ title: "Re-verificar usuario", label: "Motivo", required: true });
    if (!reason) return;
    await api.post(`/admin/users/${uid}/reset-verification`, { reason }); toast.success("Volverá a verificarse"); load();
  };
  const del = async (uid) => {
    const ok = await confirm({ title: "Eliminar usuario", description: "Se libera la cédula. Esta acción es irreversible.", destructive: true, confirmText: "Eliminar" });
    if (!ok) return;
    await api.delete(`/admin/users/${uid}`); toast.success("Eliminado"); load();
  };
  const setRole = async (uid, role) => {
    await api.post(`/admin/users/${uid}/role?role=${role}`); toast.success(`Ahora es ${role}`); load();
  };
  const filterBtn = (id, label, count, tone) => (
    <button
      key={id}
      data-testid={`users-filter-${id}`}
      onClick={() => setFilter(id)}
      className={`px-3 py-1.5 rounded text-xs border ${filter === id ? "bg-[#EF4444] border-[#EF4444] text-white" : "bg-[#0A0A0A] border-[#262626] text-neutral-400 hover:text-white"}`}
    >
      {label} <span className={`ml-1 ${filter === id ? "text-white" : tone}`}>{count}</span>
    </button>
  );

  return (
    <Section title={`Usuarios (${list.length})`}>
      {infoUser && <UserInfoModal user={infoUser} onClose={() => setInfoUser(null)} />}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {filterBtn("all",        "Todos",        counts.all,        "text-neutral-500")}
        {filterBtn("unverified", "Sin verificar", counts.unverified, "text-amber-400")}
        {filterBtn("verified",   "Verificados",   counts.verified,   "text-emerald-400")}
        {filterBtn("banned",     "Baneados",      counts.banned,     "text-red-400")}
        <input
          data-testid="users-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar por nombre, email, teléfono o cédula…"
          className="ml-auto bg-[#0A0A0A] border border-[#262626] text-white rounded h-8 px-2 text-xs w-64 max-w-full"
        />
      </div>
      <div className="bg-[#111] border border-[#262626] rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-neutral-500 text-xs uppercase border-b border-[#262626]"><tr><th className="text-left p-3">Nombre</th><th className="text-left p-3">Email</th><th className="text-left p-3">Teléfono</th><th className="text-left p-3">Estado</th><th className="text-center p-3">Strikes</th><th></th></tr></thead>
          <tbody>
            {visible.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-neutral-500 text-sm">Sin resultados con este filtro.</td></tr>
            )}
            {visible.map(u => {
              const v = VERIF_LABEL[u.verification_status] || { text: u.verification_status || "—", cls: "text-neutral-400" };
              return (
              <tr key={u.id} className="border-b border-[#171717]" data-testid={`user-row-${u.id}`}>
                <td className="p-3">
                  {/* Nombre clickeable → catálogo público del vendedor.
                      Solo lo linkeamos si está verificado (aprobado) — sin
                      eso la ruta pública devuelve 404. Los no verificados
                      quedan como texto plano. */}
                  {u.verification_status === "aprobado" ? (
                    <Link
                      to={`/vendedor/${u.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`user-catalog-link-${u.id}`}
                      className="text-[#EF4444] hover:underline"
                      title="Ver catálogo público del vendedor"
                    >
                      {u.name}
                    </Link>
                  ) : (
                    <span>{u.name}</span>
                  )}
                  {u.role === "admin" && <span className="text-[10px] bg-[#EF4444] text-white px-1.5 py-0.5 rounded ml-1">ADMIN</span>}
                  <button onClick={() => setInfoUser(u)} className="block text-xs text-amber-300 hover:underline mt-0.5" data-testid={`user-info-btn-${u.id}`}>Ver ficha</button>
                </td>
                <td className="p-3 text-neutral-400">{u.email}</td>
                <td className="p-3 text-neutral-300 whitespace-nowrap" data-testid={`user-phone-${u.id}`}>
                  {u.phone ? <>{u.phone}<WaButton phone={u.phone} testid={`user-whatsapp-${u.id}`} /></> : <span className="text-neutral-600">—</span>}
                </td>
                <td className="p-3">
                  {u.banned
                    ? <span className="text-red-400 font-semibold" title={u.banned_reason || ""}>BANEADO</span>
                    : <span className={v.cls} data-testid={`user-verif-${u.id}`}>{v.text}</span>}
                </td>
                <td className="p-3 text-center"><button type="button" className="underline decoration-dotted hover:text-amber-300" onClick={() => setStrikesUser(u)} data-testid={`strikes-open-${u.id}`}>{u.strikes || 0}</button></td>
                <td className="p-3 text-right">
                  {u.banned && u.role !== "admin" ? (
                    <div className="flex gap-1 flex-wrap justify-end">
                      <Button size="sm" variant="ghost" data-testid={`unban-${u.id}`} onClick={() => setUnbanTarget(u)} className="text-emerald-400 hover:text-emerald-300 h-7 px-2 text-xs">Desbanear</Button>
                      <Button size="sm" variant="ghost" data-testid={`delete-${u.id}`} onClick={() => del(u.id)} className="text-neutral-500 hover:text-[#EF4444] h-7 px-2"><Trash2 size={12} /></Button>
                    </div>
                  ) : !u.banned && u.role !== "admin" && (
                    <div className="flex gap-1 flex-wrap justify-end">
                      <Button size="sm" variant="ghost" onClick={() => strike(u.id)} className="text-amber-400 h-7 px-2 text-xs">+Strike</Button>
                      <Button size="sm" variant="ghost" onClick={() => resetVerif(u.id)} className="text-blue-400 h-7 px-2 text-xs">Re-verif</Button>
                      <Button size="sm" variant="ghost" onClick={() => setRole(u.id, u.role === "moderator" ? "user" : "moderator")} className="text-purple-400 h-7 px-2 text-xs">{u.role === "moderator" ? "-Mod" : "+Mod"}</Button>
                      <Button size="sm" variant="ghost" data-testid={`ban-${u.id}`} onClick={() => setBanTarget(u)} className="text-[#EF4444] h-7 px-2 text-xs">Banear</Button>
                      <Button size="sm" variant="ghost" data-testid={`delete-${u.id}`} onClick={() => del(u.id)} className="text-neutral-500 hover:text-[#EF4444] h-7 px-2"><Trash2 size={12} /></Button>
                    </div>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <ReasonPickerDialog
        open={!!banTarget}
        onOpenChange={(o) => !o && setBanTarget(null)}
        title={banTarget ? `Banear a ${banTarget.name || banTarget.email}` : ""}
        description="El motivo elegido se registra en auditoría, se guarda en la lista negra y se envía por correo al usuario."
        presets={BAN_REASONS}
        submitLabel="Banear y notificar"
        destructive
        onSubmit={submitBan}
      />
      <ReasonPickerDialog
        open={!!unbanTarget}
        onOpenChange={(o) => !o && setUnbanTarget(null)}
        title={unbanTarget ? `Desbanear a ${unbanTarget.name || unbanTarget.email}` : ""}
        description="Se libera la cédula de la lista negra y el usuario recibe un correo con el motivo del desbaneo."
        presets={UNBAN_REASONS}
        submitLabel="Desbanear y notificar"
        extraContext={unbanTarget?.banned_reason ? (
          <div className="text-xs bg-red-500/10 border border-red-500/30 rounded p-2 text-red-300">
            <b>Motivo del baneo previo:</b> {unbanTarget.banned_reason}
          </div>
        ) : null}
        onSubmit={submitUnban}
      />
      {strikesUser && <UserStrikesDialog user={strikesUser} onClose={() => setStrikesUser(null)} onChanged={load} />}
    </Section>
  );
}

function Blacklist() {
  const [list, setList] = useState([]);
  const { confirm } = useDialog();
  const load = () => api.get("/admin/blacklist").then(r => setList(r.data));
  useEffect(() => { load(); }, []);
  const remove = async (id) => {
    const ok = await confirm({ title: "Quitar de lista negra", description: "La cédula podrá volver a registrarse.", destructive: true });
    if (!ok) return;
    await api.delete(`/admin/blacklist/${id}`); toast.success("Quitado"); load();
  };
  return (
    <Section title={`Lista negra de cédulas (${list.length})`}>
      {list.length === 0 && <div className="text-neutral-500">Vacía.</div>}
      <div className="space-y-2">
        {list.map(b => (
          <div key={b.id} className="bg-[#111] border border-[#262626] rounded-md p-3 flex justify-between items-center">
            <div>
              <div className="font-mono">{b.cedula}</div>
              <div className="text-xs text-neutral-500">{b.reason} · {new Date(b.banned_at).toLocaleDateString("es-EC")}</div>
            </div>
            <Button size="sm" variant="ghost" onClick={() => remove(b.id)} className="text-neutral-400">Quitar</Button>
          </div>
        ))}
      </div>
    </Section>
  );
}

function AuditLog() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/admin/audit-log").then(r => setList(r.data)); }, []);
  return (
    <Section title="Log de auditoría">
      <div className="bg-[#111] border border-[#262626] rounded-md overflow-hidden max-h-[600px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="text-neutral-500 text-xs uppercase border-b border-[#262626] sticky top-0 bg-[#111]"><tr><th className="text-left p-3">Fecha</th><th className="text-left p-3">Acción</th><th className="text-left p-3">Target</th><th className="text-left p-3">Detalles</th></tr></thead>
          <tbody>
            {list.map(l => (
              <tr key={l.id} className="border-b border-[#171717]">
                <td className="p-3 text-neutral-500 text-xs">{new Date(l.created_at).toLocaleString("es-EC")}</td>
                <td className="p-3 font-mono text-xs">{l.action}</td>
                <td className="p-3 text-neutral-400 text-xs">{l.target_type} {l.target_id?.slice(0, 8)}</td>
                <td className="p-3 text-neutral-500 text-xs font-mono">{JSON.stringify(l.details)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

function Config() {
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState({});
  const [bank, setBank] = useState({ bank_name: "", account_type: "ahorros", account_number: "", holder_name: "HitTCG", holder_id: "", instructions: "" });
  const [transferHours, setTransferHours] = useState(1);
  const [savingBank, setSavingBank] = useState(false);
  const load = () => api.get("/admin/config").then(r => {
    setList(r.data);
    const bi = r.data.find(c => c.key === "hittcg_bank_info");
    if (bi && bi.value && typeof bi.value === "object") setBank({ ...bank, ...bi.value });
    const th = r.data.find(c => c.key === "transfer_deadline_hours");
    if (th && Number(th.value) > 0) setTransferHours(Number(th.value));
    // eslint-disable-next-line
  });
  useEffect(() => { load(); }, []);
  const save = async (k) => {
    const v = edit[k]; if (v === undefined) return;
    await api.put("/admin/config", { key: k, value: parseFloat(v) });
    toast.success("Guardado"); load();
  };
  const saveBank = async () => {
    if (!bank.bank_name.trim() || !bank.account_number.trim()) {
      toast.error("Banco y N° de cuenta son obligatorios");
      return;
    }
    setSavingBank(true);
    try {
      await api.put("/admin/config", { key: "hittcg_bank_info", value: bank });
      await api.put("/admin/config", { key: "transfer_deadline_hours", value: Number(transferHours) });
      toast.success("Datos bancarios de HitTCG guardados");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSavingBank(false);
    }
  };
  const exportCsv = async () => {
    const token = localStorage.getItem("hittcg_token");
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/export/movements.csv`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "movements.csv"; a.click();
  };
  const reseedLegal = async () => {
    try {
      const r = await api.post("/admin/legal/reseed-from-code");
      const applied = r.data?.applied || [];
      const ok = applied.filter((a) => a.status === "applied");
      const miss = applied.filter((a) => a.status === "missing_file");
      const bits = ok.map((a) => `${a.slug} (${a.chars} chars)`).join(", ");
      if (miss.length) {
        toast.warning(`Sincronizados: ${bits}. Sin archivo: ${miss.map((m) => m.slug).join(", ")}`);
      } else {
        toast.success(`Documentos legales sincronizados desde el código: ${bits}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo sincronizar");
    }
  };
  return (
    <Section title="Configuración" actions={<Button data-testid="admin-export-csv" onClick={exportCsv} variant="outline" className="border-[#262626]"><Download size={14} className="mr-2" />Exportar CSV</Button>}>
      <div className="mb-4 p-4 bg-[#0A0A0A] border border-emerald-500/30 rounded-md" data-testid="hittcg-bank-config">
        <div className="text-sm font-medium text-emerald-200 mb-1 flex items-center gap-2">
          <Landmark size={14} />Cuenta bancaria de HitTCG (transferencias)
        </div>
        <div className="text-xs text-neutral-400 mb-3">
          Los datos que ven los compradores cuando eligen pagar por transferencia. Si están vacíos, el checkout NO ofrece esta opción.
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-neutral-400 uppercase tracking-wider mb-1 block">Banco</Label>
            <Input data-testid="hittcg-bank-name" value={bank.bank_name} onChange={(e) => setBank(b => ({ ...b, bank_name: e.target.value }))} placeholder="Ej: Pichincha" className="bg-[#171717] border-[#262626]" />
          </div>
          <div>
            <Label className="text-xs text-neutral-400 uppercase tracking-wider mb-1 block">Tipo</Label>
            <Select value={bank.account_type} onValueChange={(v) => setBank(b => ({ ...b, account_type: v }))}>
              <SelectTrigger data-testid="hittcg-bank-type" className="bg-[#171717] border-[#262626]"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#171717] border-[#262626] text-white">
                <SelectItem value="ahorros">Ahorros</SelectItem>
                <SelectItem value="corriente">Corriente</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-neutral-400 uppercase tracking-wider mb-1 block">N° cuenta</Label>
            <Input data-testid="hittcg-bank-account" value={bank.account_number} onChange={(e) => setBank(b => ({ ...b, account_number: e.target.value.replace(/\D/g, "") }))} placeholder="Solo números" className="bg-[#171717] border-[#262626] font-mono" />
          </div>
          <div>
            <Label className="text-xs text-neutral-400 uppercase tracking-wider mb-1 block">Titular</Label>
            <Input data-testid="hittcg-bank-holder" value={bank.holder_name} onChange={(e) => setBank(b => ({ ...b, holder_name: e.target.value }))} placeholder="HitTCG" className="bg-[#171717] border-[#262626]" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs text-neutral-400 uppercase tracking-wider mb-1 block">RUC / Cédula del titular</Label>
            <Input data-testid="hittcg-bank-holderid" value={bank.holder_id} onChange={(e) => setBank(b => ({ ...b, holder_id: e.target.value.replace(/\D/g, "") }))} placeholder="13 dígitos (RUC) o 10 (cédula)" className="bg-[#171717] border-[#262626] font-mono" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs text-neutral-400 uppercase tracking-wider mb-1 block">Instrucciones opcionales</Label>
            <Input data-testid="hittcg-bank-notes" value={bank.instructions} onChange={(e) => setBank(b => ({ ...b, instructions: e.target.value }))} placeholder="Ej: incluye tu cédula en el detalle de la transferencia" className="bg-[#171717] border-[#262626]" />
          </div>
          <div>
            <Label className="text-xs text-neutral-400 uppercase tracking-wider mb-1 block">Plazo comprobante (horas)</Label>
            <Input data-testid="hittcg-transfer-hours" type="number" min="1" max="72" value={transferHours} onChange={(e) => setTransferHours(e.target.value)} className="bg-[#171717] border-[#262626] font-mono" />
            <div className="text-[10px] text-neutral-500 mt-1">Tiempo máximo para que el comprador suba el comprobante. Default 1h.</div>
          </div>
        </div>
        <Button size="sm" onClick={saveBank} disabled={savingBank} className="mt-3 bg-[#EF4444] hover:bg-[#DC2626]" data-testid="hittcg-bank-save">
          <BusyLabel busy={savingBank} text="Guardando…">Guardar cuenta HitTCG</BusyLabel>
        </Button>
      </div>
      <div className="mb-4 p-4 bg-[#0A0A0A] border border-amber-500/30 rounded-md" data-testid="legal-reseed-block">
        <div className="text-sm font-medium text-amber-200 mb-1">Documentos legales</div>
        <div className="text-xs text-neutral-400 mb-3">
          Aplica en esta BD el texto guardado como archivos en el repo (Términos, Privacidad, Artículos Prohibidos, Imágenes y PI). Úsalo tras un Deploy si el footer muestra texto viejo. <strong className="text-amber-300">La DB manda:</strong> los documentos editados desde este panel se saltan automáticamente y NUNCA se sobrescriben.
        </div>
        <Button
          type="button"
          size="sm"
          onClick={reseedLegal}
          data-testid="admin-legal-reseed"
          className="bg-amber-600 hover:bg-amber-700"
        >
          Re-sembrar Legales (respeta ediciones manuales)
        </Button>
      </div>
      <div className="space-y-2">
        {list
          .filter((c) => {
            // Ocultar los configs con UI dedicada (arriba) + cualquier valor
            // no-escalar. Los cuatro con editor dedicado se filtran para no
            // duplicar controles.
            if (c.key === "hittcg_bank_info"
                || c.key === "commission_tiers"
                || c.key === "min_photos_per_publication"
                || c.key === "partner_stores_settings") return false;
            return c.value === null || ["string", "number", "boolean"].includes(typeof c.value);
          })
          .map((c) => (
            <div key={c.key} className="bg-[#111] border border-[#262626] rounded-md p-3 flex items-center gap-3">
              <div className="flex-1 text-sm"><b>{c.key}</b> <span className="text-neutral-500 ml-2">actual: {String(c.value ?? "")}</span></div>
              <Input data-testid={`admin-config-${c.key}`} defaultValue={String(c.value ?? "")} onChange={(e) => setEdit(x => ({ ...x, [c.key]: e.target.value }))} className="w-32 bg-[#171717] border-[#262626]" />
              <Button size="sm" onClick={() => save(c.key)} className="bg-[#EF4444] hover:bg-[#DC2626]">Guardar</Button>
            </div>
          ))}
      </div>
    </Section>
  );
}

// ================ REACTIVATION EMAIL ================
// One-shot email to users who registered but never uploaded their ID. The UI
// fetches the target count, then sends in batches automatically to respect
// Resend's 2 req/s free-plan cap. Fully idempotent via the backend's
// `reactivation_email_sent_at` flag.
function Reactivation() {
  const { confirm } = useDialog();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sentSoFar, setSentSoFar] = useState(0);
  const [remaining, setRemaining] = useState(null);
  const [batchLog, setBatchLog] = useState([]);

  const BATCH_SIZE = 50;
  const COOLDOWN_MS = 3000; // pause between batches to protect domain reputation

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/reactivation-email/preview");
      setPreview(r.data);
      setRemaining(r.data.target_count);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo cargar el conteo");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const send = async () => {
    if (!preview || preview.target_count === 0) return;
    const ok = await confirm({
      title: `Enviar correo de reactivación a ${preview.target_count} usuarios`,
      message: `Se enviará en tandas de ${BATCH_SIZE} con pausas de ${COOLDOWN_MS / 1000}s entre tandas para respetar el límite de Resend. El envío es idempotente — cada usuario recibe el correo una sola vez.`,
      confirmText: "Enviar ahora",
    });
    if (!ok) return;
    setSending(true);
    setSentSoFar(0);
    setBatchLog([]);
    let done = 0;
    let batchNum = 0;
    while (true) {
      batchNum++;
      try {
        const r = await api.post("/admin/reactivation-email/send", {
          confirmation: "ENVIAR REACTIVACION",
          max_send: BATCH_SIZE,
        });
        const s = r.data.sent || 0;
        const rem = r.data.remaining_target || 0;
        done += s;
        setSentSoFar(done);
        setRemaining(rem);
        setBatchLog((log) => [...log, { n: batchNum, sent: s, remaining: rem, at: new Date().toLocaleTimeString("es-EC") }]);
        if (s === 0 || rem === 0) break;
        // Wait between batches to protect Resend reputation
        await new Promise(res => setTimeout(res, COOLDOWN_MS));
      } catch (err) {
        toast.error(err.response?.data?.detail || `Falló la tanda ${batchNum}`);
        break;
      }
    }
    setSending(false);
    toast.success(`Envío terminado — ${done} correos enviados`);
    await load();
  };

  if (loading) return <Section title="Correo de reactivación"><div className="text-neutral-500">Cargando conteo…</div></Section>;
  if (!preview) return <Section title="Correo de reactivación"><div className="text-red-400">No se pudo cargar</div></Section>;

  const total = preview.target_count;
  const alreadySent = preview.already_sent_count;

  return (
    <Section title="Correo de reactivación">
      <div className="mb-4 p-4 bg-blue-500/10 border border-blue-500/40 rounded-md text-sm text-blue-100 leading-relaxed">
        Envía UN correo a los usuarios que se registraron pero <b>nunca subieron la cédula</b>, avisándoles que ahora pueden explorar el catálogo sin verificarse (verificación solo al comprar/publicar). Idempotente: cada usuario recibe el correo <b>una sola vez</b>. Se envía en tandas automáticas de {BATCH_SIZE} para respetar el límite de Resend.
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div className="bg-[#111] border border-[#EF4444]/40 rounded-md p-5" data-testid="reactivation-target-card">
          <div className="text-neutral-500 text-xs uppercase tracking-wider">A enviar</div>
          <div className="font-display text-5xl text-[#EF4444] mt-2" data-testid="reactivation-target-count">{total}</div>
          <div className="text-xs text-neutral-500 mt-1">Usuarios pendientes de recibir el correo</div>
        </div>
        <div className="bg-[#111] border border-[#262626] rounded-md p-5" data-testid="reactivation-sent-card">
          <div className="text-neutral-500 text-xs uppercase tracking-wider">Ya recibieron</div>
          <div className="font-display text-5xl text-neutral-300 mt-2">{alreadySent}</div>
          <div className="text-xs text-neutral-500 mt-1">Correos enviados en corridas anteriores</div>
        </div>
      </div>

      {preview.sample_first_10?.length > 0 && (
        <div className="mb-6">
          <div className="text-xs text-neutral-500 mb-2 uppercase tracking-wider">Primeros 10 (muestra)</div>
          <div className="bg-[#111] border border-[#262626] rounded-md p-3 text-xs font-mono max-h-48 overflow-y-auto">
            {preview.sample_first_10.map((u, i) => (
              <div key={i} className="flex justify-between py-0.5 border-b border-[#1a1a1a] last:border-0">
                <span className="text-neutral-300">{u.email}</span>
                <span className="text-amber-400">{u.verification_status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {sending && (
        <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/40 rounded-md" data-testid="reactivation-progress">
          <div className="flex items-center gap-2 text-amber-300 font-semibold mb-2">
            <Clock size={16} className="animate-pulse" /> Enviando… {sentSoFar} / {total}
          </div>
          <div className="w-full bg-[#0A0A0A] rounded-full h-2 overflow-hidden">
            <div className="bg-[#EF4444] h-full transition-all" style={{ width: `${Math.min(100, (sentSoFar / Math.max(1, total)) * 100)}%` }} />
          </div>
          {batchLog.length > 0 && (
            <div className="mt-3 text-[11px] text-neutral-400 font-mono max-h-24 overflow-y-auto">
              {batchLog.slice(-6).map((b) => (
                <div key={b.n}>Tanda #{b.n} · {b.at} · {b.sent} enviados · quedan {b.remaining}</div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-3 items-center">
        <Button
          onClick={send}
          disabled={sending || total === 0}
          data-testid="reactivation-send-btn"
          className="bg-[#EF4444] hover:bg-[#DC2626] disabled:opacity-50"
        >
          <Send size={14} className="mr-2" />
          <BusyLabel busy={sending} text="Enviando…">{total === 0 ? "Nada por enviar" : `Enviar a ${total} usuarios`}</BusyLabel>
        </Button>
        <Button
          onClick={load}
          disabled={sending}
          variant="outline"
          data-testid="reactivation-refresh-btn"
          className="border-[#262626]"
        >
          Recargar conteo
        </Button>
        {remaining !== null && remaining === 0 && total === 0 && alreadySent > 0 && (
          <span className="text-xs text-emerald-400">✓ Todos los usuarios objetivo ya recibieron el correo.</span>
        )}
      </div>
    </Section>
  );
}


function TestData() {
  const { confirm } = useDialog();
  const [wipeInput, setWipeInput] = useState("");
  const [wiping, setWiping] = useState(false);
  const hide = async () => {
    const ok = await confirm({ title: "Ocultar datos de prueba", description: "No se borran, se pueden restaurar." });
    if (!ok) return;
    await api.post("/admin/test-data/hide"); toast.success("Ocultos");
  };
  const unhide = async () => { await api.post("/admin/test-data/unhide"); toast.success("Restaurados"); };
  const del = async () => {
    const step1 = await confirm({ title: "Eliminar permanentemente", description: "Se borrarán TODOS los datos marcados como prueba. Irreversible.", destructive: true, confirmText: "Sí, eliminar" });
    if (!step1) return;
    const step2 = await confirm({ title: "Confirmación final", description: "¿Estás 100% seguro? Ya no hay vuelta atrás.", destructive: true, confirmText: "Eliminar ahora" });
    if (!step2) return;
    await api.delete("/admin/test-data"); toast.success("Eliminados");
  };
  const wipeAll = async () => {
    if (wipeInput !== "BORRAR TODO") return toast.error('Tienes que tipear exactamente "BORRAR TODO"');
    const step1 = await confirm({ title: "☢ Wipe completo de la base", description: "Se borrarán TODOS los usuarios (excepto tu admin), cartas, publicaciones, pedidos, pagos, calificaciones, disputas, archivos, logs y colas. NO se puede deshacer sin el respaldo JSON que devuelve la operación. ¿Continuar?", destructive: true, confirmText: "Sí, borrar todo" });
    if (!step1) return;
    const step2 = await confirm({ title: "Última confirmación", description: 'Se hará un respaldo JSON descargable ANTES de borrar. ¿Ejecuto el wipe ahora?', destructive: true, confirmText: "Ejecutar wipe" });
    if (!step2) return;
    setWiping(true);
    try {
      const r = await api.post("/admin/wipe-all-data", { confirmation: "BORRAR TODO" });
      // Download backup as JSON
      const blob = new Blob([JSON.stringify(r.data.backup, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `hittcg-wipe-backup-${new Date().toISOString().replace(/[:.]/g, "-")}.json`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      const total = Object.values(r.data.summary).reduce((sum, v) => sum + (typeof v === "number" ? v : 0), 0);
      toast.success(`Wipe completo — ${total} documentos eliminados. Respaldo JSON descargado.`);
      setWipeInput("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al ejecutar wipe");
    } finally { setWiping(false); }
  };
  return (
    <Section title="Datos de prueba">
      <div className="bg-[#111] border border-[#262626] rounded-md p-6 space-y-4">
        <p className="text-neutral-400 text-sm">Los datos sembrados están marcados internamente como <b className="text-white">is_test: true</b>. Puedes ocultarlos temporalmente o eliminarlos por completo antes de producción. Los datos de usuarios reales que se registran nunca son marcados como test.</p>
        <div className="flex flex-wrap gap-3">
          <Button data-testid="test-data-hide" onClick={hide} variant="outline" className="border-[#262626]"><EyeOff size={14} className="mr-2" />Ocultar datos de prueba</Button>
          <Button data-testid="test-data-unhide" onClick={unhide} variant="outline" className="border-[#262626]"><Eye size={14} className="mr-2" />Restaurar</Button>
          <Button data-testid="test-data-delete" onClick={del} variant="outline" className="border-[#EF4444] text-[#EF4444]"><Trash2 size={14} className="mr-2" />Eliminar permanentemente</Button>
        </div>
      </div>

      {/* Nuclear wipe */}
      <div className="mt-6 bg-red-950/30 border-2 border-red-600/60 rounded-md p-6 space-y-4" data-testid="wipe-all-section">
        <div className="flex items-start gap-3">
          <div className="text-red-400 text-3xl leading-none">☢</div>
          <div className="flex-1">
            <h3 className="font-display text-xl text-red-300">Wipe COMPLETO de la base</h3>
            <p className="text-sm text-red-200/80 mt-1">
              Borra <b>TODO</b> excepto tu cuenta de admin, la configuración, los juegos y las páginas legales. Genera un JSON de respaldo descargable ANTES de borrar. Usar solo para arrancar limpio (fin de beta, reset de producción).
            </p>
            <ul className="text-xs text-red-200/70 mt-2 space-y-0.5 list-disc pl-4">
              <li>SE BORRA: usuarios (menos admin), cartas, publicaciones, pedidos, pagos, movimientos, disputas, calificaciones, strikes, tickets, archivos, notificaciones, logs, colas, snapshots de precio, blacklist</li>
              <li>SE PRESERVA: tu admin y sus datos (cédula, cobros, teléfono), config, games, legal</li>
            </ul>
          </div>
        </div>
        <div>
          <Label className="text-xs text-red-200 mb-1.5 block">Para habilitar el botón, tipea exactamente: <b className="font-mono text-white">BORRAR TODO</b></Label>
          <Input
            data-testid="wipe-all-input"
            value={wipeInput}
            onChange={(e) => setWipeInput(e.target.value)}
            placeholder="BORRAR TODO"
            className="bg-black border-red-500/50 text-white font-mono max-w-xs"
          />
        </div>
        <Button
          data-testid="wipe-all-btn"
          onClick={wipeAll}
          disabled={wipeInput !== "BORRAR TODO" || wiping}
          className="bg-red-700 hover:bg-red-800 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <Trash2 size={14} className="mr-2" />
          {wiping ? "Wipeando… (no cierres la pestaña)" : "☢ Ejecutar wipe completo + descargar respaldo"}
        </Button>
      </div>
    </Section>
  );
}

function AdminProfile() {
  return (
    <Section title="Mi perfil (admin)">
      <div className="space-y-4">
        <div className="bg-purple-500/10 border border-purple-500/30 rounded-md p-4 text-sm text-purple-200 flex items-start gap-2">
          <ShieldAlert size={16} className="shrink-0 mt-0.5" />
          <div>
            Como admin puedes editar tu identidad, ubicación y contacto aquí. Para configurar tus <b>Datos de Cobro</b> y <b>Envíos</b> y poder publicar cartas, entra a tu <a href="/seller?tab=profile" className="text-[#EF4444] underline font-semibold">Panel de Vendedor</a>. El admin usa las mismas herramientas que un vendedor normal.
          </div>
        </div>
        <EditProfileCard />
      </div>
    </Section>
  );
}




// ============================================================
// ADMIN TRANSFERS — auditoría + confirmación de transferencias bancarias
// ============================================================
function AdminTransfers() {
  const [list, setList] = useState([]);
  const [status, setStatus] = useState("PAGO_POR_VERIFICAR");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmAmount, setConfirmAmount] = useState("");
  const [confirmNotes, setConfirmNotes] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const { open: openLightbox } = useLightbox();
  const { confirm: askConfirm } = useDialog();

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/transfers/audit", { params: status ? { status } : {} });
      setList(r.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo cargar");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const openDetail = (o) => {
    setSelected(o);
    setConfirmAmount(String(Number(o.total || 0).toFixed(2)));
    setConfirmNotes("");
    setRejectReason("");
  };

  const doConfirm = async () => {
    const amt = Number(confirmAmount);
    if (!(amt > 0)) { toast.error("Monto inválido"); return; }
    const ok = await askConfirm({
      title: "Confirmar transferencia",
      description: `Marcarás el pedido como PAGADO con $${amt.toFixed(2)} recibidos. Esto no se puede deshacer. ¿Continuar?`,
      confirmText: "Confirmar pago",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.post(`/admin/orders/${selected.id}/transfer/confirm`, { amount_received: amt, notes: confirmNotes });
      toast.success("Transferencia confirmada — el pedido pasa a PAGADO");
      setSelected(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo confirmar");
    } finally {
      setBusy(false);
    }
  };

  const doReject = async () => {
    if (!rejectReason.trim()) { toast.error("Ingresa la razón del rechazo"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/orders/${selected.id}/transfer/reject`, { reason: rejectReason.trim() });
      toast.success("Comprobante rechazado — el comprador puede reintentar");
      setSelected(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo rechazar");
    } finally {
      setBusy(false);
    }
  };

  const totalExpected = list.reduce((s, o) => s + Number(o.total || 0), 0);
  const totalReported = list.reduce((s, o) => s + Number(o.transfer_receipt?.amount_reported || 0), 0);

  return (
    <Section title={`Transferencias bancarias (${list.length})`}>
      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <select
          data-testid="admin-transfers-filter"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="bg-[#171717] border border-[#262626] rounded px-3 py-1 text-sm"
        >
          <option value="">Todas</option>
          <option value="PENDIENTE_PAGO">Pendientes (sin comprobante)</option>
          <option value="PAGO_POR_VERIFICAR">Por verificar</option>
          <option value="PAGADO">Confirmadas (Pagadas)</option>
          <option value="CANCELADO">Canceladas</option>
        </select>
        <div className="text-xs text-neutral-500">Esperado: <b className="text-white">${totalExpected.toFixed(2)}</b> · Reportado: <b className="text-white">${totalReported.toFixed(2)}</b></div>
      </div>
      {loading ? (
        <div className="text-neutral-500 text-sm">Cargando…</div>
      ) : list.length === 0 ? (
        <div className="text-neutral-500 text-sm">Sin transferencias en este estado.</div>
      ) : (
        <div className="bg-[#111] border border-[#262626] rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-neutral-500 text-xs uppercase border-b border-[#262626]">
              <tr>
                <th className="text-left p-3">Pedido</th>
                <th className="text-left p-3">Comprador</th>
                <th className="text-left p-3">Estado</th>
                <th className="text-right p-3">Esperado</th>
                <th className="text-right p-3">Reportado</th>
                <th className="text-left p-3">Cuadra</th>
                <th className="text-left p-3">Fecha</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {list.map((o) => {
                const r = o.transfer_receipt || {};
                const reported = Number(r.amount_reported || 0);
                const expected = Number(o.total || 0);
                const diff = Math.abs(reported - expected);
                const matches = reported > 0 && diff < 0.01;
                return (
                  <tr key={o.id} className="border-b border-[#171717] hover:bg-[#0A0A0A] cursor-pointer" onClick={() => openDetail(o)} data-testid={`transfer-row-${o.id}`}>
                    <td className="p-3 font-mono text-xs text-neutral-500">#{o.id.slice(0, 8)}</td>
                    <td className="p-3">{o.buyer_name || "—"}</td>
                    <td className="p-3"><span className={`status-pill status-${(o.status || "").toLowerCase().replace(/_/g, "-")}`}>{o.status}</span>
                      {o.transfer_urgent && o.status === "PAGO_POR_VERIFICAR" && <span className="ml-1 text-[10px] font-bold text-red-300 bg-red-900/50 px-1.5 py-0.5 rounded" data-testid={`transfer-urgent-${o.id}`}>REVISIÓN URGENTE</span>}</td>
                    <td className="p-3 text-right font-mono text-white">${expected.toFixed(2)}</td>
                    <td className={`p-3 text-right font-mono ${reported > 0 ? "text-white" : "text-neutral-600"}`}>{reported > 0 ? `$${reported.toFixed(2)}` : "—"}</td>
                    <td className="p-3">
                      {reported === 0 ? <span className="text-neutral-600 text-xs">—</span>
                        : matches ? <span className="text-emerald-400 text-xs inline-flex items-center gap-1"><Check size={12} />OK</span>
                        : <span className="text-red-400 text-xs inline-flex items-center gap-1"><X size={12} />${diff.toFixed(2)}</span>}
                    </td>
                    <td className="p-3 text-xs text-neutral-400">{r.uploaded_at ? new Date(r.uploaded_at).toLocaleString("es-EC") : "—"}</td>
                    <td className="p-3 text-right"><span className="text-[#EF4444] text-xs">Ver →</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-40 bg-black/70 flex items-start justify-center p-4 overflow-y-auto" onClick={() => !busy && setSelected(null)}>
          <div className="bg-[#0A0A0A] border border-[#262626] rounded-md max-w-2xl w-full p-6 my-8" onClick={(e) => e.stopPropagation()} data-testid="transfer-detail-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-2xl">Pedido #{selected.id.slice(0, 8)}</h3>
              <button className="text-neutral-500 hover:text-white" onClick={() => !busy && setSelected(null)}><X size={20} /></button>
            </div>
            <div className="text-sm space-y-2 mb-4">
              <div><span className="text-neutral-500">Comprador:</span> <b className="text-white">{selected.buyer_name}</b> <span className="text-neutral-500">({selected.buyer_email})</span></div>
              <div><span className="text-neutral-500">Vendedor:</span> <b className="text-white">{selected.seller_name}</b></div>
              <div><span className="text-neutral-500">Carta:</span> <b className="text-white">{selected.card_name || "—"}</b></div>
              <div><span className="text-neutral-500">Estado actual:</span> <span className={`status-pill status-${(selected.status || "").toLowerCase().replace(/_/g, "-")}`}>{selected.status}</span></div>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-4 p-3 bg-[#111] border border-[#262626] rounded">
              <div>
                <div className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Monto esperado</div>
                <div className="font-display text-2xl text-white font-mono">${Number(selected.total).toFixed(2)}</div>
              </div>
              <div>
                <div className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Reportado por comprador</div>
                <div className="font-display text-2xl text-white font-mono">${Number(selected.transfer_receipt?.amount_reported || 0).toFixed(2)}</div>
              </div>
            </div>
            {selected.transfer_receipt && (
              <div className="mb-4 p-3 bg-[#111] border border-[#262626] rounded text-sm space-y-1">
                <div className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Comprobante</div>
                <div><span className="text-neutral-500">N°:</span> <b className="text-white font-mono">{selected.transfer_receipt.receipt_number}</b></div>
                <div><span className="text-neutral-500">Desde:</span> <b className="text-white">{selected.transfer_receipt.bank_from}</b></div>
                <div><span className="text-neutral-500">Subido:</span> {new Date(selected.transfer_receipt.uploaded_at).toLocaleString("es-EC")}</div>
                {selected.transfer_receipt.receipt_path && (
                  <div className="mt-3">
                    <button
                      type="button"
                      className="text-[#EF4444] hover:underline text-xs inline-flex items-center gap-1"
                      onClick={() => openLightbox([selected.transfer_receipt.receipt_path], 0)}
                      data-testid="transfer-view-receipt"
                    >
                      <FileText size={12} />Ver comprobante
                    </button>
                  </div>
                )}
              </div>
            )}
            {selected.transfer_verification?.status === "rejected" && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded text-xs text-red-200">
                <b>Comprobante rechazado previamente:</b> {selected.transfer_verification.reject_reason}
              </div>
            )}
            {selected.transfer_verification?.status === "confirmed" && (
              <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded text-xs text-emerald-200">
                Confirmado el {new Date(selected.transfer_verification.verified_at).toLocaleString("es-EC")} con ${Number(selected.transfer_verification.amount_received).toFixed(2)} recibidos.
                {selected.transfer_verification.notes && <div className="mt-1"><b>Notas:</b> {selected.transfer_verification.notes}</div>}
              </div>
            )}
            {selected.status === "PAGO_POR_VERIFICAR" && (
              <>
                <div className="border-t border-[#262626] pt-4 mt-4 space-y-3">
                  <div className="text-xs text-neutral-400 leading-relaxed">
                    <b className="text-amber-300">Antes de confirmar, cotéjalo en el banco:</b> abre el estado de cuenta de HitTCG y verifica que efectivamente entró la transferencia con el N° <b className="text-white font-mono">{selected.transfer_receipt?.receipt_number}</b>.
                  </div>
                  <div>
                    <Label className="text-xs text-neutral-400 uppercase tracking-wider mb-1 block">Monto REAL recibido en la cuenta</Label>
                    <Input data-testid="transfer-confirm-amount" type="number" step="0.01" min="0" value={confirmAmount} onChange={(e) => setConfirmAmount(e.target.value)} className="bg-[#171717] border-[#262626] font-mono" />
                    <div className="text-[10px] text-neutral-500 mt-1">Debe cuadrar EXACTAMENTE con el esperado (${Number(selected.total).toFixed(2)}) — sin tolerancia.</div>
                  </div>
                  <div>
                    <Label className="text-xs text-neutral-400 uppercase tracking-wider mb-1 block">Notas internas (opcional)</Label>
                    <Input data-testid="transfer-confirm-notes" value={confirmNotes} onChange={(e) => setConfirmNotes(e.target.value)} placeholder="Ej: verificado en Pichincha 15:32" className="bg-[#171717] border-[#262626]" />
                  </div>
                  <Button onClick={doConfirm} disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="transfer-confirm-btn">
                    <Check size={16} className="mr-2" />
                    <BusyLabel busy={busy} text="Confirmando…">Confirmar pago (marcar PAGADO)</BusyLabel>
                  </Button>
                </div>
                <div className="border-t border-[#262626] pt-4 mt-4 space-y-3">
                  <div>
                    <Label className="text-xs text-neutral-400 uppercase tracking-wider mb-1 block">Razón del rechazo</Label>
                    <Input data-testid="transfer-reject-reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Ej: monto no cuadra, comprobante ilegible, no se ve el depósito en cuenta" className="bg-[#171717] border-[#262626]" />
                  </div>
                  <Button onClick={doReject} disabled={busy || !rejectReason.trim()} variant="ghost" className="w-full text-red-400 hover:bg-red-500/10 border border-red-500/30" data-testid="transfer-reject-btn">
                    <XCircle size={16} className="mr-2" />
                    <BusyLabel busy={busy} text="Rechazando…">Rechazar (vuelve a Pendiente de pago)</BusyLabel>
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </Section>
  );
}
